/**
 * Support & Ticketing Module — Module #4  (v5 — Progressive Resolution UX)
 * Sliding Context workspace, chip-based config, brand-aggregator linking, no priority.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Routes, Route, useNavigate, useSearchParams } from 'react-router-dom';
import { usePersistentState } from '../../hooks/usePersistentState';
import {
  LayoutDashboard, Inbox, BookOpen, Settings, Plus, Search, X, Upload,
  ChevronRight, ChevronDown, ChevronLeft, Clock, Send, AlertCircle, CheckCircle2,
  MessageSquare, MessageCircle, BarChart3, TrendingUp, ArrowLeft, ArrowRight,
  User as UserIcon, Building2, Tag, FileText, LifeBuoy, XCircle, Image, Package2,
  Layers, Check, Trash2, Eye, Paperclip, AtSign, Globe, BookOpenCheck, Pencil,
  ExternalLink, CircleDot, Ban, HelpCircle, Activity, RotateCcw, DollarSign, Gift,
  ChevronUp, Edit3, Grip, Download, Repeat, Shield, Zap, Target, Users, Flame,
  Calendar, Info, Hourglass, ShieldCheck, TriangleAlert
} from 'lucide-react';
import { User, UserRole, NotificationType, Department, Brand } from '../../domain/entities';
import { ModuleTabs, TabGate } from '../shared/ModuleTabs';
import { hasTabVariant } from '../../core/security';
import { getRepository } from '../../config/repository';
import { uploadFile, getMediaUrl } from '../../core/fileUpload';
import { useToast } from '../../ui/Toast';
import { cn } from '../../ui/utils';
import { ConfirmDeleteDialog, type DeleteConfirmState } from '../../ui/ConfirmDeleteDialog';
import {
  Ticket, TicketStatus, TicketMessage, StatusHistoryEntry,
  IssueReport, IssueModule, IssueCategory, IssueAttachment,
  IssueReportStatus, SupportSettings, SupportDeskConfig,
  ResolutionRecord,
  ISSUE_MODULE_LABELS, ISSUE_MODULE_REQUIRED_APP,
  ISSUE_CATEGORY_LABELS, ISSUE_CATEGORY_ICONS, ISSUE_CATEGORY_COLORS,
  TICKET_STATUS_LABELS,
  STATUS_COLORS, STATUS_LADDER,
  AGGREGATOR_PLATFORMS, AGGREGATOR_COLORS, MENTIONABLE_DEPARTMENTS,
  ISSUE_REPORT_STATUS_LABELS, ISSUE_REPORT_STATUS_COLORS,
  deriveTicketStatus,
} from './types';
import { BlobStore } from '../../data/BlobStore';
import type { Campaign, CampaignSettings } from '../aggregator/types';

// --- Lucide Icon Map ----------------------------------------------------------

const CATEGORY_ICON_MAP: Record<string, React.ElementType> = {
  Tag, Image, AlertCircle, Gift, Package2, DollarSign, Clock, FileText, Settings,
};

function CategoryIcon({ name, className }: { name: string; className?: string }) {
  const Icon = CATEGORY_ICON_MAP[name] || HelpCircle;
  return <Icon className={className} />;
}

// --- Helpers ------------------------------------------------------------------

function generateTicketId(): string {
  return `TKT-${Math.floor(1000 + Math.random() * 9000)}`;
}

function generateTicketTitle(reports: IssueReport[]): string {
  if (reports.length === 0) return 'Untitled Ticket';
  const first = ISSUE_CATEGORY_LABELS[reports[0].category];
  if (reports.length === 1) return first;
  return `${first} (+${reports.length - 1} more)`;
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatDuration(ms: number): string {
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  return `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function issueResolutionSummary(reports: IssueReport[]) {
  const resolved = reports.filter(r => r.status === IssueReportStatus.RESOLVED).length;
  const rejected = reports.filter(r => r.status === IssueReportStatus.REJECTED).length;
  const pending = reports.filter(r => r.status === IssueReportStatus.PENDING).length;
  return { resolved, rejected, pending, total: reports.length };
}

/** Pure utility: calculate how long a ticket has been in its current status stage. */
function calculateTimeInStage(ticket: Ticket): { currentStatus: TicketStatus; durationMs: number; enteredAt: number } {
  const history = ticket.statusHistory ?? [];
  if (history.length === 0) {
    return { currentStatus: ticket.status, durationMs: Date.now() - ticket.createdAt, enteredAt: ticket.createdAt };
  }
  const last = history[history.length - 1];
  return { currentStatus: last.status, durationMs: Date.now() - last.timestamp, enteredAt: last.timestamp };
}

/** Pure utility: get the timestamp when a ticket entered a specific status (first time). */
function getStatusTimestamp(ticket: Ticket, status: TicketStatus): number | null {
  const history = ticket.statusHistory ?? [];
  const entry = history.find(h => h.status === status);
  return entry ? entry.timestamp : null;
}

/** Ensure backward compat: tickets loaded from old localStorage may lack statusHistory. */
function ensureStatusHistory(ticket: Ticket): Ticket {
  if (ticket.statusHistory && ticket.statusHistory.length > 0) return ticket;
  const history: StatusHistoryEntry[] = [
    { status: TicketStatus.NEW, timestamp: ticket.createdAt, changedBy: ticket.managerId, changedByName: ticket.managerName },
  ];
  // Infer history from SLA timestamps
  if (ticket.sla.firstResponseAt) {
    history.push({ status: TicketStatus.OPEN, timestamp: ticket.sla.firstResponseAt, changedBy: 'system', changedByName: 'System' });
  }
  if (ticket.status === TicketStatus.IN_PROGRESS && !ticket.sla.resolvedAt) {
    const ts = ticket.sla.firstResponseAt ? ticket.sla.firstResponseAt + 60000 : ticket.createdAt + 120000;
    history.push({ status: TicketStatus.IN_PROGRESS, timestamp: ts, changedBy: 'system', changedByName: 'System' });
  }
  if (ticket.sla.resolvedAt) {
    history.push({ status: TicketStatus.RESOLVED, timestamp: ticket.sla.resolvedAt, changedBy: 'system', changedByName: 'System' });
  }
  if (ticket.status === TicketStatus.CLOSED) {
    history.push({ status: TicketStatus.CLOSED, timestamp: ticket.updatedAt, changedBy: 'system', changedByName: 'System' });
  }
  return { ...ticket, statusHistory: history };
}

// --- Smart Aggregator Detection -----------------------------------------------
// Reads aggregator module campaigns to find which aggregators a brand is linked to.
// Falls back to full list if no aggregator module access.

async function detectBrandAggregators(brandIds: string[], orgEnabledApps?: string[]): Promise<{ linked: string[]; hasAccess: boolean }> {
  const hasAggAccess = !orgEnabledApps || orgEnabledApps.includes('aggregator');
  if (!hasAggAccess || brandIds.length === 0) return { linked: [], hasAccess: hasAggAccess };
  try {
    const repo = getRepository();
    const campaigns: Campaign[] = await repo.getCampaigns();
    const linked = new Set<string>();
    for (const c of campaigns) {
      const matchesBrand = c.targetAllBrands || c.brandIds.some(bid => brandIds.includes(bid));
      if (matchesBrand) c.aggregators.forEach(a => linked.add(a));
    }
    return { linked: [...linked].sort(), hasAccess: true };
  } catch {
    return { linked: [], hasAccess: false };
  }
}

// --- Data Access (localStorage) -----------------------------------------------

const STORAGE_KEY_TICKETS = 'dm_support_tickets_v5';
const STORAGE_KEY_SETTINGS = 'dm_support_settings';
const STORAGE_KEY_DESK_CONFIG = 'dm_support_desk_config';

function loadTicketsRaw(): Ticket[] {
  try { return (JSON.parse(localStorage.getItem(STORAGE_KEY_TICKETS) || '[]') as Ticket[]).map(ensureStatusHistory); }
  catch { return []; }
}

/** Hydrate blob:// refs in attachments back to full base64 */
async function loadTickets(): Promise<Ticket[]> {
  const raw = loadTicketsRaw();
  for (const t of raw) {
    for (const ir of t.issueReports) {
      for (let i = 0; i < ir.attachments.length; i++) {
        const att = ir.attachments[i];
        if (BlobStore.isBlobRef(att.data)) {
          const data = await BlobStore.getBlob(att.data);
          ir.attachments[i] = { ...att, data: data ?? '' };
        }
      }
    }
  }
  return raw;
}

/** Externalize large attachment data to IndexedDB, then save slim JSON to localStorage */
async function saveTickets(tickets: Ticket[]): Promise<void> {
  const slim = tickets.map(t => ({
    ...t,
    issueReports: t.issueReports.map(ir => ({
      ...ir,
      attachments: ir.attachments.map(att => ({ ...att })),
    })),
  }));
  for (const t of slim) {
    for (const ir of t.issueReports) {
      for (let i = 0; i < ir.attachments.length; i++) {
        const att = ir.attachments[i];
        if (att.data.length > 1024 && !att.data.startsWith('blob://')) {
          const key = `blob://${crypto.randomUUID()}`;
          await BlobStore.putBlob(key, att.data);
          ir.attachments[i] = { ...att, data: key };
        }
      }
    }
  }
  localStorage.setItem(STORAGE_KEY_TICKETS, JSON.stringify(slim));
}
function loadSettings(): SupportSettings {
  try { const d = localStorage.getItem(STORAGE_KEY_SETTINGS); if (d) return JSON.parse(d); } catch {}
  return { slaTargets: { firstResponse: 30, resolution: 480 } };
}
function saveSettings(s: SupportSettings): void { localStorage.setItem(STORAGE_KEY_SETTINGS, JSON.stringify(s)); }

const DEFAULT_DESK_CONFIG: SupportDeskConfig = {
  issueTypes: Object.values(IssueCategory).map(cat => ({ id: cat, label: ISSUE_CATEGORY_LABELS[cat], icon: ISSUE_CATEGORY_ICONS[cat] })),
  systemAreas: Object.values(IssueModule).map(mod => ({ id: mod, label: ISSUE_MODULE_LABELS[mod] })),
  slaTargets: { firstResponse: 30, resolution: 480 },
  autoAssign: true,
  emailNotifications: false,
  criticalCategories: [IssueCategory.OUT_OF_STOCK, IssueCategory.MISSING_ITEM],
  autoCloseDurations: [
    { label: '24 Hours', hours: 24 },
    { label: '48 Hours', hours: 48 },
    { label: '72 Hours', hours: 72 },
  ],
};
function loadDeskConfig(): SupportDeskConfig {
  try {
    const d = localStorage.getItem(STORAGE_KEY_DESK_CONFIG);
    if (d) {
      const parsed = JSON.parse(d);
      return {
        ...DEFAULT_DESK_CONFIG,
        ...parsed,
        criticalCategories: parsed.criticalCategories ?? DEFAULT_DESK_CONFIG.criticalCategories,
        autoCloseDurations: parsed.autoCloseDurations ?? DEFAULT_DESK_CONFIG.autoCloseDurations,
      };
    }
  } catch {}
  return { ...DEFAULT_DESK_CONFIG };
}
function saveDeskConfig(c: SupportDeskConfig): void { localStorage.setItem(STORAGE_KEY_DESK_CONFIG, JSON.stringify(c)); }

// --- Demo Data (DISABLED — production mode) -----------------------------------

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function seedDemoTickets(_userId: string, _userName: string): Ticket[] {
  // Production: no demo tickets. Return empty array.
  return [];
}

// --- Module Root --------------------------------------------------------------

interface Props { user: User | null; orgEnabledApps?: string[]; }

export function SupportDeskModule({ user, orgEnabledApps }: Props) {
  const base = '/apps/support-desk';

  const [openTicketCount, setOpenTicketCount] = useState(0);
  useEffect(() => {
    loadTickets().then(tickets => {
      setOpenTicketCount(tickets.filter(t => t.status === TicketStatus.NEW || t.status === TicketStatus.OPEN || t.status === TicketStatus.IN_PROGRESS || t.status === TicketStatus.PENDING).length);
    });
  }, []);

  const moduleTabs = [
    { key: 'dashboard', label: 'Dashboard', to: base, exact: true },
    { key: 'ticket-center', label: 'Ticket Center', to: `${base}/ticket-center`, exact: false, badge: openTicketCount },
    { key: 'track-lifecycle', label: 'Track Lifecycle', to: `${base}/track-lifecycle`, exact: false },
    { key: 'operational-insights', label: 'Operational Insights', to: `${base}/operational-insights`, exact: false },
    { key: 'knowledge-base', label: 'Knowledge Base', to: `${base}/knowledge-base`, exact: false },
    { key: 'settings', label: 'App Settings', to: `${base}/settings`, exact: false },
  ];

  return (
    <div className="space-y-0">
      <ModuleTabs moduleId="support-desk" tabs={moduleTabs} user={user} />
      <div className="pt-4">
        <Routes>
          <Route index element={<TabGate moduleId="support-desk" tabId="dashboard" user={user}><DashboardTab user={user} /></TabGate>} />
          <Route path="ticket-center/*" element={<TabGate moduleId="support-desk" tabId="ticket-center" user={user}><TicketCenterTab user={user} orgEnabledApps={orgEnabledApps} /></TabGate>} />
          <Route path="track-lifecycle" element={<TabGate moduleId="support-desk" tabId="track-lifecycle" user={user}><TrackLifecycleTab user={user} /></TabGate>} />
          <Route path="operational-insights" element={<TabGate moduleId="support-desk" tabId="operational-insights" user={user}><OperationalInsightsTab user={user} /></TabGate>} />
          <Route path="knowledge-base" element={<TabGate moduleId="support-desk" tabId="knowledge-base" user={user}><KnowledgeBaseTab user={user} /></TabGate>} />
          <Route path="settings" element={<TabGate moduleId="support-desk" tabId="settings" user={user}><AppSettingsTab user={user} /></TabGate>} />
        </Routes>
      </div>
    </div>
  );
}

// --- Dashboard Tab (Support Command Center) -----------------------------------

function DashboardTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'support-desk', 'dashboard', 'admin');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const navigate = useNavigate();
  const deskConfig = useMemo(() => loadDeskConfig(), []);
  const toast = useToast();

  useEffect(() => {
    (async () => {
      let all = await loadTickets();
      if (!all.length && user) { all = seedDemoTickets(user.id, user.name); await saveTickets(all); }
      setTickets(all);
      const repo = getRepository();
      const b = await repo.getBrands();
      setBrands(b.filter(br => br.enabledModules?.includes('support-desk')));
    })();
  }, [user]);

  // --- Scoped data ---
  const myTickets = isAdmin ? tickets : tickets.filter(t => t.managerId === user?.id);
  const allIssues = myTickets.flatMap(t => t.issueReports);
  const now = Date.now();
  const DAY = 86400000;

  // --- KPI metrics ---
  // Card 1: Unassigned Queue (NEW tickets)
  const unassignedQueue = myTickets.filter(t => t.status === TicketStatus.NEW).length;

  // Card 2: Active Resolutions (IN_PROGRESS + PENDING)
  const activeResolutions = myTickets.filter(t => t.status === TicketStatus.IN_PROGRESS || t.status === TicketStatus.PENDING).length;

  // Card 3: First Response Time (avg NEW → OPEN)
  const ticketsWithResponse = myTickets.filter(t => t.sla.firstResponseAt);
  const avgFirstResponseMs = ticketsWithResponse.length > 0
    ? ticketsWithResponse.reduce((sum, t) => sum + (t.sla.firstResponseAt! - t.sla.createdAt), 0) / ticketsWithResponse.length : 0;

  // Card 4: Critical Aggregator Alert (config-driven critical categories)
  const criticalAlerts = allIssues.filter(ir =>
    deskConfig.criticalCategories.includes(ir.category)
  ).length;

  // Card 5: New Tickets Received Today
  const newTicketsToday = myTickets.filter(t => now - t.createdAt < DAY).length;

  // --- SLA Overdue Detection ---
  const slaResolutionMs = deskConfig.slaTargets.resolution * 60 * 1000; // stored in minutes
  const overdueTickets = myTickets.filter(t =>
    !t.sla.resolvedAt
    && t.status !== TicketStatus.CLOSED
    && t.status !== TicketStatus.RESOLVED
    && (now - t.sla.createdAt) > slaResolutionMs
  );

  // --- Resolution Rate ---
  const resolved30 = allIssues.filter(ir => ir.status === IssueReportStatus.RESOLVED).length;
  const resolutionRate = allIssues.length > 0 ? Math.round((resolved30 / allIssues.length) * 100) : 0;

  // --- Bottleneck Analysis: Avg time in each status ---
  const bottleneckData = useMemo(() => {
    const statusTimes: Record<string, number[]> = {};
    STATUS_LADDER.forEach(s => { statusTimes[s] = []; });
    myTickets.forEach(ticket => {
      const history = ticket.statusHistory ?? [];
      for (let i = 0; i < history.length; i++) {
        const entry = history[i];
        const nextTs = i < history.length - 1 ? history[i + 1].timestamp : (
          ticket.status === entry.status ? now : entry.timestamp
        );
        const duration = nextTs - entry.timestamp;
        if (duration > 0 && statusTimes[entry.status]) {
          statusTimes[entry.status].push(duration);
        }
      }
      // If still in current status, add time since last entry
      if (history.length > 0) {
        const last = history[history.length - 1];
        if (last.status === ticket.status) {
          const dur = now - last.timestamp;
          if (dur > 0 && statusTimes[last.status]) statusTimes[last.status].push(dur);
        }
      }
    });
    const results = STATUS_LADDER.map(status => {
      const times = statusTimes[status];
      const avg = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
      return { status, avgMs: avg, count: times.length };
    }).filter(r => r.count > 0);
    const maxAvg = results.length > 0 ? Math.max(...results.map(r => r.avgMs)) : 1;
    return results.map(r => ({ ...r, pct: Math.round((r.avgMs / maxAvg) * 100) }));
  }, [myTickets, now]);

  // --- Aggregator Volume Heatmap ---
  const aggVolume: { name: string; count: number; pct: number }[] = (() => {
    const map: Record<string, number> = {};
    allIssues.forEach(ir => ir.aggregators.forEach(a => { map[a] = (map[a] || 0) + 1; }));
    const entries = Object.entries(map).sort((a, b) => b[1] - a[1]);
    const max = entries.length > 0 ? entries[0][1] : 1;
    return entries.map(([name, count]) => ({ name, count, pct: Math.round((count / max) * 100) }));
  })();

  // --- Issue Type Distribution (Pie data) ---
  const issueTypeDist = Object.values(IssueCategory).map(cat => ({
    category: cat, label: ISSUE_CATEGORY_LABELS[cat], icon: ISSUE_CATEGORY_ICONS[cat],
    count: allIssues.filter(ir => ir.category === cat).length,
  })).filter(c => c.count > 0).sort((a, b) => b.count - a.count);
  const issueTypeTotal = issueTypeDist.reduce((s, c) => s + c.count, 0);

  // --- Top 5 Impacted Brands ---
  const brandIssueMap: Record<string, number> = {};
  myTickets.filter(t => t.status !== TicketStatus.RESOLVED && t.status !== TicketStatus.CLOSED).forEach(t => {
    t.issueReports.forEach(ir => ir.brandIds.forEach(bid => { brandIssueMap[bid] = (brandIssueMap[bid] || 0) + 1; }));
  });
  const topBrands = Object.entries(brandIssueMap)
    .sort((a, b) => b[1] - a[1]).slice(0, 5)
    .map(([bid, count]) => ({ id: bid, name: brands.find(b => b.id === bid)?.name ?? bid, count }));

  // --- Latest System Alerts ---
  const latestAlerts = [...myTickets]
    .sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 5)
    .map(t => {
      const lastMsg = t.messages[t.messages.length - 1];
      const text = lastMsg
        ? `${lastMsg.senderName}: "${lastMsg.content.slice(0, 60)}${lastMsg.content.length > 60 ? '...' : ''}"`
        : `${t.managerName} submitted "${t.subject}"`;
      return { id: t.id, text, time: t.updatedAt, status: t.status };
    });

  // --- Pie chart colors ---
  const PIE_COLORS = ['bg-indigo-500', 'bg-violet-500', 'bg-sky-500', 'bg-amber-500', 'bg-emerald-500', 'bg-rose-500', 'bg-teal-500', 'bg-orange-500', 'bg-slate-400'];
  const PIE_TEXT = ['text-indigo-500', 'text-violet-500', 'text-sky-500', 'text-amber-500', 'text-emerald-500', 'text-rose-500', 'text-teal-500', 'text-orange-500', 'text-slate-400'];

  // --- Navigators ---
  const goTicketCenter = (filter?: string, tab?: string) => {
    const params = new URLSearchParams();
    if (filter) params.set('filter', filter);
    if (tab) params.set('tab', tab);
    const qs = params.toString();
    navigate(`/apps/support-desk/ticket-center${qs ? `?${qs}` : ''}`);
  };
  const goTicket = (id: string) => navigate(`/apps/support-desk/ticket-center?open=${id}`);

  // Quick status change from dashboard
  const quickSetStatus = async (ticketId: string, newStatus: TicketStatus) => {
    const idx = tickets.findIndex(t => t.id === ticketId);
    if (idx < 0) return;
    const t = tickets[idx];
    const nowTs = Date.now();
    const updated: Ticket = {
      ...t,
      status: newStatus,
      statusHistory: [...(t.statusHistory ?? []), { status: newStatus, timestamp: nowTs, changedBy: user?.id ?? 'system', changedByName: user?.name ?? 'System' }],
      sla: { ...t.sla, firstResponseAt: t.sla.firstResponseAt ?? nowTs },
      updatedAt: nowTs,
    };
    const newTickets = [...tickets]; newTickets[idx] = updated;
    await saveTickets(newTickets); setTickets(newTickets);
    toast.success('Status Updated', `${ticketId} → ${TICKET_STATUS_LABELS[newStatus]}`);
  };

  // --- Empty State ---
  if (myTickets.length === 0) {
    return (
      <div className="px-6 pb-8">
        <div className="py-20 flex flex-col items-center gap-4">
          <div className="w-20 h-20 bg-indigo-50 rounded-3xl flex items-center justify-center">
            <CheckCircle2 size={40} className="text-indigo-400" />
          </div>
          <h2 className="text-xl font-bold text-slate-900">Quiet Day</h2>
          <p className="text-sm text-slate-500 max-w-sm text-center">No issues reported yet. All systems are running smoothly.</p>
          <button onClick={() => goTicketCenter()} className="mt-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-sm">
            Go to Ticket Center
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 px-6 pb-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold tracking-tight text-slate-900">
          {isAdmin ? 'Support Command Center' : 'Your Brands Health Overview'}
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">
          {isAdmin ? 'Organization-wide support analytics and operational health.' : 'Track your support requests and brand health.'}
        </p>
      </div>

      {/* KPI Row — 5 cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Unassigned Queue */}
        <button onClick={() => goTicketCenter(undefined, 'action')} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm text-left hover:border-sky-300 hover:shadow-md transition-all group">
          <div className="flex items-center justify-between mb-3">
            <div className="w-9 h-9 bg-sky-50 rounded-xl flex items-center justify-center group-hover:bg-sky-100 transition-colors">
              <Inbox size={18} className="text-sky-600" />
            </div>
            {unassignedQueue > 0 && (
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-sky-500" />
              </span>
            )}
          </div>
          <div className="text-2xl font-black text-slate-900">{unassignedQueue}</div>
          <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mt-1">Unassigned Queue</div>
        </button>

        {/* Active Resolutions */}
        <button onClick={() => goTicketCenter('IN_PROGRESS')} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm text-left hover:border-indigo-300 hover:shadow-md transition-all group">
          <div className="flex items-center justify-between mb-3">
            <div className="w-9 h-9 bg-indigo-50 rounded-xl flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
              <Activity size={18} className="text-indigo-600" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900">{activeResolutions}</div>
          <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mt-1">Active Resolutions</div>
        </button>

        {/* First Response Time */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center">
              <Clock size={18} className="text-blue-600" />
            </div>
            <span className="text-[10px] text-slate-400 font-medium">Avg</span>
          </div>
          <div className="text-2xl font-black text-slate-900">{avgFirstResponseMs > 0 ? formatDuration(avgFirstResponseMs) : '—'}</div>
          <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mt-1">First Response Time</div>
        </div>

        {/* Critical Aggregator Alert */}
        <button onClick={() => goTicketCenter(undefined, 'action')} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm text-left hover:border-red-300 hover:shadow-md transition-all group">
          <div className="flex items-center justify-between mb-3">
            <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center group-hover:bg-red-100 transition-colors">
              <AlertCircle size={18} className="text-red-600" />
            </div>
            {criticalAlerts > 0 && (
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
              </span>
            )}
          </div>
          <div className="text-2xl font-black text-slate-900">{criticalAlerts}</div>
          <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mt-1">Critical Alerts</div>
        </button>

        {/* New Tickets Today */}
        <button onClick={() => goTicketCenter('NEW')} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm text-left hover:border-emerald-300 hover:shadow-md transition-all group">
          <div className="flex items-center justify-between mb-3">
            <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
              <Plus size={18} className="text-emerald-600" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900">{newTicketsToday}</div>
          <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mt-1">Received Today</div>
          <div className="mt-2.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${resolutionRate}%` }} />
          </div>
          <div className="text-[9px] text-slate-400 mt-1">{resolutionRate}% resolved</div>
        </button>

        {/* SLA Overdue */}
        <button onClick={() => goTicketCenter(undefined, 'active')} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm text-left hover:border-amber-300 hover:shadow-md transition-all group">
          <div className="flex items-center justify-between mb-3">
            <div className="w-9 h-9 bg-amber-50 rounded-xl flex items-center justify-center group-hover:bg-amber-100 transition-colors">
              <Hourglass size={18} className="text-amber-600" />
            </div>
            {overdueTickets.length > 0 && (
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
              </span>
            )}
          </div>
          <div className="text-2xl font-black text-slate-900">{overdueTickets.length}</div>
          <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mt-1">SLA Overdue</div>
          <div className="text-[9px] text-slate-400 mt-1">Target: {deskConfig.slaTargets.resolution >= 60 ? `${Math.round(deskConfig.slaTargets.resolution / 60)}h` : `${deskConfig.slaTargets.resolution}m`}</div>
        </button>
      </div>

      {/* Bottleneck Analysis + Aggregator Volume Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Bottleneck Analysis — Avg Time per Status */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900 mb-1">Bottleneck Analysis</h3>
          <p className="text-[10px] text-slate-400 mb-4">Average time spent in each status stage</p>
          {bottleneckData.length > 0 ? (
            <div className="space-y-3">
              {bottleneckData.map(item => (
                <div key={item.status}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className={cn("w-2 h-2 rounded-full", STATUS_COLORS[item.status].dot)} />
                      <span className={cn("text-xs font-semibold", STATUS_COLORS[item.status].text)}>{TICKET_STATUS_LABELS[item.status]}</span>
                    </div>
                    <span className="text-xs font-bold text-slate-700">{formatDuration(item.avgMs)}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={cn("h-full rounded-full transition-all", STATUS_COLORS[item.status].dot.replace('bg-', 'bg-'))}
                      style={{ width: `${item.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic py-4 text-center">No lifecycle data yet.</p>
          )}
        </div>

        {/* Aggregator Volume Heatmap */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900 mb-1">Aggregator Issue Volume</h3>
          <p className="text-[10px] text-slate-400 mb-4">Which platforms generate the most reports</p>
          {aggVolume.length > 0 ? (
            <div className="space-y-2.5">
              {aggVolume.map(agg => (
                <button key={agg.name} onClick={() => goTicketCenter(agg.name)}
                  className="w-full group">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-700 group-hover:text-indigo-600 transition-colors">{agg.name}</span>
                    <span className="text-xs font-bold text-slate-900">{agg.count}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500 rounded-full transition-all group-hover:bg-indigo-600" style={{ width: `${agg.pct}%` }} />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic py-4 text-center">No aggregator data yet.</p>
          )}
        </div>
      </div>

      {/* Issue Type Pie + Top Brands Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Issue Type Distribution */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900 mb-1">Issue Type Distribution</h3>
          <p className="text-[10px] text-slate-400 mb-4">Breakdown of problem categories</p>
          {issueTypeDist.length > 0 ? (
            <div className="flex gap-5">
              {/* Visual ring */}
              <div className="relative w-28 h-28 shrink-0">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  {(() => {
                    let offset = 0;
                    return issueTypeDist.map((cat, idx) => {
                      const pct = (cat.count / issueTypeTotal) * 100;
                      const dash = `${pct} ${100 - pct}`;
                      const el = (
                        <circle key={cat.category} r="15.9" cx="18" cy="18" fill="none" strokeWidth="3.5"
                          className={PIE_COLORS[idx % PIE_COLORS.length].replace('bg-', 'stroke-')}
                          strokeDasharray={dash} strokeDashoffset={`${-offset}`} strokeLinecap="round" />
                      );
                      offset += pct;
                      return el;
                    });
                  })()}
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-lg font-black text-slate-900">{issueTypeTotal}</div>
                    <div className="text-[8px] text-slate-400 font-semibold uppercase">Issues</div>
                  </div>
                </div>
              </div>
              {/* Legend */}
              <div className="flex-1 space-y-1.5 overflow-hidden">
                {issueTypeDist.map((cat, idx) => (
                  <button key={cat.category} onClick={() => goTicketCenter(cat.category)}
                    className="w-full flex items-center justify-between py-1 hover:bg-slate-50 rounded-lg px-1 transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={cn("w-2.5 h-2.5 rounded-sm shrink-0", PIE_COLORS[idx % PIE_COLORS.length])} />
                      <span className="text-[11px] font-medium text-slate-700 truncate">{cat.label}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={cn("text-[11px] font-bold", PIE_TEXT[idx % PIE_TEXT.length])}>{Math.round((cat.count / issueTypeTotal) * 100)}%</span>
                      <span className="text-[10px] text-slate-400">{cat.count}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic py-4 text-center">No issue data yet.</p>
          )}
        </div>

        {/* Top Impacted Brands */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900 mb-1">{isAdmin ? 'Top Impacted Brands' : 'Your Brand Issues'}</h3>
          <p className="text-[10px] text-slate-400 mb-4">Brands with the most open tickets</p>
          {topBrands.length > 0 ? (
            <div className="space-y-2">
              {topBrands.map((brand, idx) => (
                <div key={brand.id} className="flex items-center gap-3 py-1.5">
                  <span className="text-[10px] font-bold text-slate-400 w-4 text-right">#{idx + 1}</span>
                  <div className="w-7 h-7 bg-indigo-50 rounded-lg flex items-center justify-center shrink-0">
                    <Building2 size={14} className="text-indigo-600" />
                  </div>
                  <span className="text-xs font-semibold text-slate-700 flex-1 truncate">{brand.name}</span>
                  <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded-full text-[10px] font-bold">{brand.count} open</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic py-4 text-center">All brands clear — no open issues.</p>
          )}
        </div>
      </div>

      {/* Latest Activity + Recent Tickets Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Latest System Alerts */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900 mb-1">Latest Activity</h3>
          <p className="text-[10px] text-slate-400 mb-4">Most recent ticket activity</p>
          {latestAlerts.length > 0 ? (
            <div className="space-y-2">
              {latestAlerts.map(alert => (
                <button key={alert.id} onClick={() => goTicket(alert.id)}
                  className="w-full flex items-start gap-3 py-2 text-left hover:bg-slate-50 rounded-lg px-1 transition-colors">
                  <div className={cn("w-2 h-2 rounded-full mt-1.5 shrink-0", STATUS_COLORS[alert.status].dot,
                    alert.status !== TicketStatus.RESOLVED && alert.status !== TicketStatus.CLOSED && "animate-pulse")} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-slate-700 line-clamp-1">{alert.text}</p>
                    <span className="text-[10px] text-slate-400">{timeAgo(alert.time)}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic py-4 text-center">No recent activity.</p>
          )}
        </div>

        {/* Quick-View Recent Tickets */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-900">Recent Tickets</h3>
            <button onClick={() => goTicketCenter()} className="text-xs font-semibold text-indigo-600 hover:underline flex items-center gap-1">
              View All <ChevronRight size={14} />
            </button>
          </div>
          <div className="divide-y divide-slate-100">
            {myTickets.sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 5).map(ticket => {
              const sum = issueResolutionSummary(ticket.issueReports);
              const isOverdue = !ticket.sla.resolvedAt && ticket.status !== TicketStatus.CLOSED && ticket.status !== TicketStatus.RESOLVED && (now - ticket.sla.createdAt) > slaResolutionMs;
              const canQuickAction = isAdmin && [TicketStatus.NEW, TicketStatus.OPEN].includes(ticket.status);
              return (
                <div key={ticket.id} className="flex items-center gap-4 px-5 py-3 hover:bg-slate-50 transition-colors">
                  <button onClick={() => goTicket(ticket.id)} className="flex items-center gap-4 flex-1 min-w-0 text-left">
                    <div className={cn("w-2 h-2 rounded-full shrink-0", STATUS_COLORS[ticket.status].dot,
                      ticket.status !== TicketStatus.RESOLVED && ticket.status !== TicketStatus.CLOSED && "animate-pulse")} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-400">{ticket.id}</span>
                        <span className="text-[10px] text-emerald-600 font-medium">{sum.resolved}/{sum.total}</span>
                        {isOverdue && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[9px] font-bold">⏰ Overdue</span>}
                      </div>
                      <div className="text-sm font-semibold text-slate-900 truncate mt-0.5">{ticket.subject}</div>
                    </div>
                  </button>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {canQuickAction && (
                      <>
                        <button onClick={() => quickSetStatus(ticket.id, TicketStatus.IN_PROGRESS)} title="Set In Progress"
                          className="px-2 py-1 rounded-lg text-[9px] font-bold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors">
                          In Progress
                        </button>
                        <button onClick={() => quickSetStatus(ticket.id, TicketStatus.PENDING)} title="Set Pending"
                          className="px-2 py-1 rounded-lg text-[9px] font-bold bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors">
                          Pending
                        </button>
                      </>
                    )}
                    <span className="text-[10px] text-slate-400">{timeAgo(ticket.updatedAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Ticket Center Tab --------------------------------------------------------

type TicketSubTab = 'action' | 'new' | 'active' | 'pending' | 'completed';
const SUB_TAB_STATUSES: Record<TicketSubTab, TicketStatus[]> = {
  action: [], // special — uses actionable filter, not status-based
  new: [TicketStatus.NEW],
  active: [TicketStatus.OPEN, TicketStatus.IN_PROGRESS],
  pending: [TicketStatus.PENDING],
  completed: [TicketStatus.RESOLVED, TicketStatus.CLOSED],
};

/**
 * Determines if a ticket needs the current user's action.
 * - Manager: RESOLVED tickets awaiting their verdict
 * - Admin: Tickets reopened by a manager dispute (OPEN with a disputed resolution)
 * - Both: Tickets with critical issues (config-driven) that are not yet closed
 */
function ticketNeedsAction(ticket: Ticket, userId: string | undefined, isAdmin: boolean, criticalCategories?: string[]): boolean {
  if (!userId) return false;
  const latestRes = ticket.resolutionHistory?.[ticket.resolutionHistory.length - 1];

  // Closed tickets never need action
  if (ticket.status === TicketStatus.CLOSED) return false;

  // Critical issue tickets (config-driven) always need attention if not resolved/closed
  const critCats = criticalCategories ?? [IssueCategory.OUT_OF_STOCK, IssueCategory.MISSING_ITEM];
  const hasCritical = ticket.issueReports.some(ir =>
    critCats.includes(ir.category)
    && ir.status === IssueReportStatus.PENDING
  );
  if (hasCritical) return true;

  if (!isAdmin) {
    // Manager side: RESOLVED ticket that I own, awaiting my verdict
    return ticket.status === TicketStatus.RESOLVED
      && ticket.managerId === userId
      && !!latestRes
      && latestRes.managerVerdict === null;
  }
  // Admin side: NEW tickets need review, disputed tickets need attention
  if (ticket.status === TicketStatus.NEW) return true;
  return ticket.status === TicketStatus.OPEN
    && !!latestRes
    && latestRes.managerVerdict === 'disputed';
}

function TicketCenterTab({ user, orgEnabledApps }: { user: User | null; orgEnabledApps?: string[] }) {
  const isAdmin = hasTabVariant(user, 'support-desk', 'ticket-center', 'admin');
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState | null>(null);
  const [searchQuery, setSearchQuery] = usePersistentState('search');
  const [activeSubTab, setActiveSubTab] = usePersistentState<TicketSubTab>('tab', 'action');
  const toast = useToast();
  const navigate = useNavigate();
  const deskConfig = useMemo(() => loadDeskConfig(), []);

  const reload = async () => {
    let all = await loadTickets();
    if (!all.length && user) { all = seedDemoTickets(user.id, user.name); await saveTickets(all); }
    setTickets(all);
    const repo = getRepository();
    const b = await repo.getBrands();
    setBrands(b.filter(br => br.enabledModules?.includes('support-desk')));
  };

  useEffect(() => { reload(); }, [user]);

  // ── Auto-Close Watchdog — checks RESOLVED tickets with expired timers ───
  useEffect(() => {
    const interval = setInterval(async () => {
      const now = Date.now();
      const all = await loadTickets();
      let changed = false;
      const updated = all.map(t => {
        if (t.status !== TicketStatus.RESOLVED || !t.autoCloseExpiry) return t;
        if (now < t.autoCloseExpiry) return t;
        // Auto-close expired ticket
        changed = true;
        const closedHistory = (t.resolutionHistory ?? []).map((r, i, arr) =>
          i === arr.length - 1 ? { ...r, autoClosedBySystem: true } : r
        );
        const statusEntry: StatusHistoryEntry = { status: TicketStatus.CLOSED, timestamp: now, changedBy: 'system', changedByName: 'System' };
        const autoMsg: TicketMessage = {
          id: crypto.randomUUID(), ticketId: t.id, senderId: 'system', senderName: 'System', senderRole: 'admin',
          content: '🔒 System auto-closed ticket due to inactivity. Manager did not respond within the auto-close window.',
          createdAt: now,
        };
        return {
          ...t, status: TicketStatus.CLOSED,
          statusHistory: [...(t.statusHistory ?? []), statusEntry],
          messages: [...t.messages, autoMsg],
          updatedAt: now, autoCloseExpiry: undefined,
          resolutionHistory: closedHistory,
        };
      });
      if (changed) {
        await saveTickets(updated);
        setTickets(updated);
      }
    }, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Handle search params (open ticket, filter from dashboard)
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId && tickets.length > 0) {
      const found = tickets.find(t => t.id === openId);
      if (found) selectTicket(found);
    }
    // ?filter= maps a status to the owning sub-tab (only if no explicit ?tab=)
    const filterParam = searchParams.get('filter');
    if (filterParam && !searchParams.get('tab')) {
      const statusValues = Object.values(TicketStatus) as string[];
      if (statusValues.includes(filterParam)) {
        const matchTab = (Object.entries(SUB_TAB_STATUSES) as [TicketSubTab, TicketStatus[]][])
          .find(([, statuses]) => statuses.includes(filterParam as TicketStatus));
        if (matchTab) setActiveSubTab(matchTab[0]);
      }
    }
  }, [tickets, searchParams]);

  const myTickets = useMemo(() => {
    return isAdmin ? tickets : tickets.filter(t => t.managerId === user?.id);
  }, [tickets, isAdmin, user]);

  // Sub-tab counts
  const tabCounts = useMemo(() => {
    const counts: Record<TicketSubTab, number> = { action: 0, new: 0, active: 0, pending: 0, completed: 0 };
    myTickets.forEach(t => {
      // Action count is independent — a ticket can be in Action AND its status-based tab
      if (ticketNeedsAction(t, user?.id, isAdmin, deskConfig.criticalCategories)) counts.action++;
      for (const [tab, statuses] of Object.entries(SUB_TAB_STATUSES) as [TicketSubTab, TicketStatus[]][]) {
        if (tab === 'action') continue;
        if (statuses.includes(t.status)) { counts[tab]++; break; }
      }
    });
    return counts;
  }, [myTickets, user, isAdmin]);

  const visibleTickets = useMemo(() => {
    let result: Ticket[];
    if (activeSubTab === 'action') {
      result = myTickets.filter(t => ticketNeedsAction(t, user?.id, isAdmin, deskConfig.criticalCategories));
    } else {
      const tabStatuses = SUB_TAB_STATUSES[activeSubTab];
      result = myTickets.filter(t => tabStatuses.includes(t.status));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t => t.id.toLowerCase().includes(q) || t.subject.toLowerCase().includes(q) || t.managerName.toLowerCase().includes(q));
    }
    return result.sort((a, b) => b.updatedAt - a.updatedAt);
  }, [myTickets, activeSubTab, searchQuery, user, isAdmin]);

  // Auto-move NEW → OPEN when admin clicks to open a NEW ticket
  const selectTicket = async (ticket: Ticket) => {
    if (isAdmin && ticket.status === TicketStatus.NEW) {
      const now = Date.now();
      const updated: Ticket = {
        ...ticket,
        status: TicketStatus.OPEN,
        statusHistory: [
          ...(ticket.statusHistory ?? []),
          { status: TicketStatus.OPEN, timestamp: now, changedBy: user?.id ?? 'system', changedByName: user?.name ?? 'System' },
        ],
        sla: { ...ticket.sla, firstResponseAt: ticket.sla.firstResponseAt ?? now },
        updatedAt: now,
      };
      const idx = tickets.findIndex(t => t.id === updated.id);
      if (idx >= 0) {
        const newTickets = [...tickets]; newTickets[idx] = updated;
        await saveTickets(newTickets); setTickets(newTickets);
      }
      setSelectedTicket(updated);
      setActiveSubTab('active');
    } else {
      setSelectedTicket(ticket);
    }
  };

  const handleCreateTicket = async (ticket: Ticket) => {
    const updated = [...tickets, ticket];
    await saveTickets(updated);
    setTickets(updated);
    setShowCreateForm(false);
    if (user) {
      const repo = getRepository();
      const users = await repo.getUsers();
      const admins = users.filter(u => (u.role === UserRole.ADMIN || u.role === UserRole.SUPER_ADMIN) && u.id !== user.id);
      for (const admin of admins) {
        await repo.saveNotification({
          id: crypto.randomUUID(), recipientId: admin.id, actorId: user.id,
          type: NotificationType.INFO,
          title: 'New Support Ticket',
          message: `${user.name} reported: "${ticket.subject}" [${ticket.id}] with ${ticket.issueReports.length} issue(s)`,
          targetPath: `/apps/support-desk/ticket-center?open=${ticket.id}`,
          isRead: false, createdAt: Date.now(),
        });
      }
      await repo.saveSystemLog({
        id: crypto.randomUUID(), userId: user.id, userName: user.name,
        action: `Created support ticket ${ticket.id}: "${ticket.subject}" with ${ticket.issueReports.length} issue(s)`,
        timestamp: Date.now(), moduleId: 'support-desk',
      });
    }
    toast.success('Ticket Created', `${ticket.id} has been submitted with ${ticket.issueReports.length} issue report(s).`);
  };

  const handleUpdateTicket = async (updated: Ticket) => {
    const idx = tickets.findIndex(t => t.id === updated.id);
    if (idx < 0) return;
    const prev = tickets[idx];
    const newTickets = [...tickets]; newTickets[idx] = updated;
    await saveTickets(newTickets); setTickets(newTickets); setSelectedTicket(updated);

    // Detect manager dispute → send high-priority notification to admins
    if (prev.status === TicketStatus.RESOLVED && updated.status === TicketStatus.OPEN && user) {
      const repo = getRepository();
      const allUsers = await repo.getUsers();
      const admins = allUsers.filter(u => (u.role === UserRole.ADMIN || u.role === UserRole.SUPER_ADMIN) && u.id !== user.id);
      const latestRes = updated.resolutionHistory?.[updated.resolutionHistory.length - 1];
      for (const admin of admins) {
        await repo.saveNotification({
          id: crypto.randomUUID(), recipientId: admin.id, actorId: user.id,
          type: NotificationType.WARNING,
          title: '⚠️ Resolution Disputed',
          message: `${user.name} disputed ticket ${updated.id}: "${latestRes?.verdictMessage || 'Still has issues'}"`,
          targetPath: `/apps/support-desk/ticket-center?open=${updated.id}`,
          isRead: false, createdAt: Date.now(),
        });
      }
    }
  };

  const handleDeleteTicket = (ticket: Ticket) => {
    setDeleteConfirm({
      message: `Delete ticket ${ticket.id}: "${ticket.subject}"? This cannot be undone.`,
      onConfirm: async () => {
        const updated = tickets.filter(t => t.id !== ticket.id);
        await saveTickets(updated); setTickets(updated); setSelectedTicket(null); setDeleteConfirm(null);
        toast.success('Deleted', `${ticket.id} removed.`);
      },
    });
  };

  const managerBrands = useMemo(() => {
    if (isAdmin) return brands;
    if (!user?.assignedBrands || user.assignedBrands.length === 0) return brands;
    return brands.filter(b => user.assignedBrands!.includes(b.id));
  }, [brands, user, isAdmin]);

  const availableModules = useMemo(() => {
    return Object.values(IssueModule).filter(mod => {
      const requiredApp = ISSUE_MODULE_REQUIRED_APP[mod];
      if (!orgEnabledApps) return true;
      return orgEnabledApps.includes(requiredApp);
    });
  }, [orgEnabledApps]);

  if (showCreateForm) {
    return <ProgressiveTicketCreator user={user} brands={managerBrands} availableModules={availableModules} orgEnabledApps={orgEnabledApps} onSubmit={handleCreateTicket} onCancel={() => setShowCreateForm(false)} />;
  }

  if (selectedTicket) {
    return <ResolutionWorkspace ticket={selectedTicket} user={user} brands={brands} onUpdateTicket={handleUpdateTicket} onBack={() => { setSelectedTicket(null); navigate('/apps/support-desk/ticket-center', { replace: true }); }} />;
  }

  const subTabs: { key: TicketSubTab; label: string; color: string; dotColor: string }[] = [
    { key: 'action', label: 'Action Center', color: 'text-rose-600', dotColor: 'bg-rose-500' },
    { key: 'new', label: 'New Tickets', color: 'text-sky-600', dotColor: 'bg-sky-500' },
    { key: 'active', label: 'Active', color: 'text-indigo-600', dotColor: 'bg-indigo-500' },
    { key: 'pending', label: 'Pending', color: 'text-orange-600', dotColor: 'bg-orange-500' },
    { key: 'completed', label: 'Completed', color: 'text-emerald-600', dotColor: 'bg-emerald-500' },
  ];

  return (
    <div className="space-y-4 px-6 pb-8">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">{isAdmin ? 'All Tickets' : 'My Tickets'}</h2>
          <p className="text-sm text-slate-500 mt-1">{isAdmin ? 'Manage, assign, and resolve support tickets.' : 'View and track your support requests.'}</p>
        </div>
        <button onClick={() => setShowCreateForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-all shadow-sm">
          <Plus size={16} /> New Ticket
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {subTabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveSubTab(tab.key)}
            className={cn("flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all -mb-px",
              activeSubTab === tab.key
                ? cn(tab.key === 'action' ? "border-rose-500" : "border-indigo-600", tab.color)
                : "border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300")}>
            {tab.key === 'action' && <Zap size={14} className={tabCounts.action > 0 ? "text-rose-500 animate-pulse" : "text-slate-400"} />}
            {tab.label}
            {tabCounts[tab.key] > 0 && (
              <span className={cn("px-1.5 py-0.5 rounded-full text-[10px] font-bold min-w-[20px] text-center",
                activeSubTab === tab.key
                  ? (tab.key === 'action' ? "bg-rose-100 text-rose-700" :
                     tab.key === 'new' ? "bg-sky-100 text-sky-700" :
                     tab.key === 'active' ? "bg-indigo-100 text-indigo-700" :
                     tab.key === 'pending' ? "bg-orange-100 text-orange-700" :
                     "bg-emerald-100 text-emerald-700")
                  : tab.key === 'action' ? "bg-rose-100 text-rose-600 animate-pulse" : "bg-slate-100 text-slate-500")}>
                {tabCounts[tab.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search tickets..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/60">
              <th className="text-left px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Ticket</th>
              <th className="text-left px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Title</th>
              <th className="text-left px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Brands</th>
              <th className="text-left px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Issues</th>
              <th className="text-left px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Resolution</th>
              <th className="text-left px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
              {isAdmin && <th className="text-left px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Reporter</th>}
              <th className="text-left px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visibleTickets.map(ticket => {
              const sum = issueResolutionSummary(ticket.issueReports);
              return (
                <tr key={ticket.id} onClick={() => selectTicket(ticket)}
                  className="hover:bg-slate-50 cursor-pointer transition-colors group">
                  <td className="px-4 py-3"><span className="text-xs font-mono font-bold text-indigo-600 group-hover:underline">{ticket.id}</span></td>
                  <td className="px-4 py-3"><div className="text-sm font-semibold text-slate-900 truncate max-w-[250px]">{ticket.subject}</div></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-[180px]">
                      {(() => {
                        const allBrandIds = [...new Set(ticket.issueReports.flatMap(ir => ir.brandIds))];
                        if (allBrandIds.length === 0) return <span className="text-[10px] text-slate-400">—</span>;
                        return allBrandIds.slice(0, 3).map(bid => {
                          const name = brands.find(b => b.id === bid)?.name ?? bid;
                          return <span key={bid} className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] text-slate-600 truncate max-w-[80px]">{name}</span>;
                        });
                      })()}
                      {(() => {
                        const allBrandIds = [...new Set(ticket.issueReports.flatMap(ir => ir.brandIds))];
                        return allBrandIds.length > 3 ? <span className="text-[10px] text-slate-400">+{allBrandIds.length - 3}</span> : null;
                      })()}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-slate-900">{sum.total}</span>
                      <div className="flex gap-0.5">
                        {([...new Set(ticket.issueReports.map(ir => ir.category))] as IssueCategory[]).slice(0, 3).map(cat => (
                          <span key={cat} className="w-5 h-5 rounded flex items-center justify-center bg-slate-100" title={ISSUE_CATEGORY_LABELS[cat]}>
                            <CategoryIcon name={ISSUE_CATEGORY_ICONS[cat]} className="w-3 h-3 text-slate-500" />
                          </span>
                        ))}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-bold text-emerald-600">{sum.resolved}</span>
                      <span className="text-[10px] text-slate-400">/</span>
                      <span className="text-xs text-slate-500">{sum.total}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <div className={cn("w-1.5 h-1.5 rounded-full", STATUS_COLORS[ticket.status].dot,
                        [TicketStatus.NEW, TicketStatus.OPEN, TicketStatus.IN_PROGRESS].includes(ticket.status) && "animate-pulse")} />
                      <span className={cn("text-xs font-semibold", STATUS_COLORS[ticket.status].text)}>{TICKET_STATUS_LABELS[ticket.status]}</span>
                      {!ticket.sla.resolvedAt && ticket.status !== TicketStatus.CLOSED && ticket.status !== TicketStatus.RESOLVED
                        && (Date.now() - ticket.sla.createdAt) > deskConfig.slaTargets.resolution * 60 * 1000
                        && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[9px] font-bold">⏰ Overdue</span>}
                    </div>
                    {activeSubTab === 'action' && (() => {
                      const latestRes = ticket.resolutionHistory?.[ticket.resolutionHistory.length - 1];
                      const hasCritical = ticket.issueReports.some(ir =>
                        deskConfig.criticalCategories.includes(ir.category)
                        && ir.status === IssueReportStatus.PENDING
                      );
                      const badges: React.ReactNode[] = [];
                      if (isAdmin && ticket.status === TicketStatus.NEW) {
                        badges.push(<span key="new" className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 text-[9px] font-bold animate-pulse">🆕 New — Review</span>);
                      }
                      if (hasCritical) {
                        badges.push(<span key="crit" className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[9px] font-bold animate-pulse">🚨 Critical</span>);
                      }
                      if (!isAdmin && ticket.status === TicketStatus.RESOLVED) {
                        badges.push(<span key="verify" className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[9px] font-bold animate-pulse">⚡ Verify Resolution</span>);
                      }
                      if (isAdmin && latestRes?.managerVerdict === 'disputed') {
                        badges.push(<span key="disp" className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[9px] font-bold animate-pulse">🔴 Disputed</span>);
                      }
                      return badges.length > 0 ? <div className="mt-1 flex items-center gap-1 flex-wrap">{badges}</div> : null;
                    })()}
                  </td>
                  {isAdmin && <td className="px-4 py-3 text-xs text-slate-500">{ticket.managerName}</td>}
                  <td className="px-4 py-3 text-xs text-slate-400">{timeAgo(ticket.updatedAt)}</td>
                </tr>
              );
            })}
            {visibleTickets.length === 0 && (
              <tr><td colSpan={isAdmin ? 8 : 7} className="text-center py-16">
                <div className="flex flex-col items-center gap-3">
                  <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center",
                    activeSubTab === 'action' ? "bg-rose-50" : "bg-indigo-50")}>
                    {activeSubTab === 'action'
                      ? <Zap size={28} className="text-rose-400" />
                      : <HelpCircle size={28} className="text-indigo-400" />}
                  </div>
                  <h3 className="text-base font-bold text-slate-900">
                    {activeSubTab === 'action' ? 'No actions needed' :
                     activeSubTab === 'new' ? 'No new tickets' :
                     activeSubTab === 'active' ? 'No active tickets' :
                     activeSubTab === 'pending' ? 'No pending tickets' :
                     'No completed tickets'}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {activeSubTab === 'action' ? 'All tickets are up to date. No actions required from you.' :
                     activeSubTab === 'new' ? 'All tickets have been reviewed.' :
                     activeSubTab === 'active' ? 'No tickets currently being worked on.' :
                     activeSubTab === 'pending' ? 'No tickets waiting for information.' :
                     'No tickets have been resolved yet.'}
                  </p>
                  {activeSubTab === 'new' && (
                    <button onClick={() => setShowCreateForm(true)} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-all mt-2 shadow-sm">
                      <Plus size={16} /> Create New Ticket
                    </button>
                  )}
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDeleteDialog state={deleteConfirm} onClose={() => setDeleteConfirm(null)} />
    </div>
  );
}

// --- Progressive Ticket Creator (Sliding Context UX) --------------------------

interface ConfigState {
  module: IssueModule;
  brandIds: string[];
  aggregators: string[];
  category: IssueCategory | null;
  manualAgg: string;
}

interface CompletedIssue {
  id: string;
  config: ConfigState;
  details: string;
  attachments: IssueAttachment[];
  editing: boolean;
}

interface CreatorProps {
  user: User | null;
  brands: Brand[];
  availableModules: IssueModule[];
  orgEnabledApps?: string[];
  onSubmit: (ticket: Ticket) => void;
  onCancel: () => void;
}

function ProgressiveTicketCreator({ user, brands, availableModules, orgEnabledApps, onSubmit, onCancel }: CreatorProps) {
  // Completed issue stack
  const [completedIssues, setCompletedIssues] = useState<CompletedIssue[]>([]);
  // Staged file refs (attachment id → raw File) — uploaded only on final submit
  const stagedFilesRef = useRef<Map<string, File>>(new Map());

  // Current issue state
  const [config, setConfig] = useState<ConfigState>({
    module: availableModules[0] ?? IssueModule.AGGREGATOR,
    brandIds: [],
    aggregators: [],
    category: null,
    manualAgg: '',
  });
  const [configApplied, setConfigApplied] = useState(false);
  const [configAnimating, setConfigAnimating] = useState<'opening' | 'closing' | null>(null);
  const [details, setDetails] = useState('');
  const [attachments, setAttachments] = useState<IssueAttachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Smart aggregator detection
  const [linkedAggs, setLinkedAggs] = useState<string[]>([]);
  const [aggAccess, setAggAccess] = useState(true);
  const [aggLoading, setAggLoading] = useState(false);

  useEffect(() => {
    if (config.brandIds.length === 0) {
      setLinkedAggs([]); setAggAccess(true); return;
    }
    setAggLoading(true);
    detectBrandAggregators(config.brandIds, orgEnabledApps).then(({ linked, hasAccess }) => {
      setLinkedAggs(linked);
      setAggAccess(hasAccess);
      // auto-clear aggregators that are no longer linked
      if (hasAccess && linked.length > 0) {
        setConfig(prev => ({ ...prev, aggregators: prev.aggregators.filter(a => linked.includes(a)) }));
      }
      setAggLoading(false);
    });
  }, [config.brandIds, orgEnabledApps]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) return;
      const id = crypto.randomUUID();
      const previewUrl = URL.createObjectURL(file);
      stagedFilesRef.current.set(id, file);
      setAttachments(prev => [...prev, { id, name: file.name, type: file.type, size: file.size, data: previewUrl }]);
    });
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files) as File[];
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) return;
      const id = crypto.randomUUID();
      const previewUrl = URL.createObjectURL(file);
      stagedFilesRef.current.set(id, file);
      setAttachments(prev => [...prev, { id, name: file.name, type: file.type, size: file.size, data: previewUrl }]);
    });
  };

  const applyConfig = () => {
    if (config.brandIds.length === 0 || config.category === null) return;
    setConfigAnimating('closing');
    setTimeout(() => { setConfigApplied(true); setConfigAnimating(null); }, 260);
  };

  const changeConfig = () => {
    setConfigApplied(false);
    setConfigAnimating('opening');
    setTimeout(() => setConfigAnimating(null), 360);
  };

  const completeCurrentIssue = () => {
    if (!details.trim()) return;
    const issue: CompletedIssue = {
      id: crypto.randomUUID(),
      config: { ...config },
      details: details.trim(),
      attachments: [...attachments],
      editing: false,
    };
    setCompletedIssues(prev => [...prev, issue]);
    // Preserve config for next issue — only clear description and attachments
    setDetails('');
    setAttachments([]);
  };

  const removeCompletedIssue = (id: string) => {
    setCompletedIssues(prev => prev.filter(i => i.id !== id));
  };

  const editCompletedIssue = (id: string) => {
    const issue = completedIssues.find(i => i.id === id);
    if (!issue) return;
    // Load it back into the editor
    setConfig(issue.config);
    setDetails(issue.details);
    setAttachments(issue.attachments);
    setConfigApplied(true);
    setCompletedIssues(prev => prev.filter(i => i.id !== id));
  };

  const allIssuesReady = completedIssues.length > 0 || (configApplied && details.trim());

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!user) return;
    setSubmitting(true);
    const now = Date.now();
    // Gather all issues (completed + current if valid)
    const allIssues = [...completedIssues];
    if (configApplied && details.trim()) {
      allIssues.push({
        id: crypto.randomUUID(),
        config: { ...config },
        details: details.trim(),
        attachments: [...attachments],
        editing: false,
      });
    }
    if (allIssues.length === 0) { setSubmitting(false); return; }

    // Upload all staged files before building the ticket
    try {
      for (const issue of allIssues) {
        for (let i = 0; i < issue.attachments.length; i++) {
          const att = issue.attachments[i];
          const staged = stagedFilesRef.current.get(att.id);
          if (staged) {
            const url = await uploadFile(staged, { moduleId: 'support-desk' });
            issue.attachments[i] = { ...att, data: url };
            stagedFilesRef.current.delete(att.id);
            URL.revokeObjectURL(att.data);
          }
        }
      }
    } catch { setSubmitting(false); return; }

    const reports: IssueReport[] = allIssues.map(i => ({
      id: crypto.randomUUID(),
      module: i.config.module,
      brandIds: [...i.config.brandIds],
      aggregators: i.config.aggregators.length > 0 ? [...i.config.aggregators] : (i.config.manualAgg ? [i.config.manualAgg] : []),
      category: i.config.category,
      details: i.details,
      attachments: [...i.attachments],
      status: IssueReportStatus.PENDING,
    }));

    const ticket: Ticket = {
      id: generateTicketId(),
      subject: generateTicketTitle(reports),
      status: TicketStatus.NEW,
      managerId: user.id,
      managerName: user.name,
      orgId: user.orgId ?? '',
      issueReports: reports,
      statusHistory: [{ status: TicketStatus.NEW, timestamp: now, changedBy: user.id, changedByName: user.name }],
      sla: { createdAt: now },
      messages: [{ id: crypto.randomUUID(), ticketId: '', senderId: user.id, senderName: user.name, senderRole: 'manager', content: `Ticket created with ${reports.length} issue report(s).`, createdAt: now }],
      createdAt: now, updatedAt: now,
    };
    ticket.messages[0].ticketId = ticket.id;
    setSubmitting(false);
    onSubmit(ticket);
  };

  const configValid = config.brandIds.length > 0 && config.category !== null;
  const brandNames = (ids: string[]) => ids.map(id => brands.find(b => b.id === id)?.name ?? id);
  const aggChips = aggAccess && linkedAggs.length > 0 ? linkedAggs : (AGGREGATOR_PLATFORMS as unknown as string[]);

  return (
    <div className="px-6 pb-8 max-w-4xl">
      <button onClick={onCancel} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-5 transition-colors">
        <ArrowLeft size={16} /> Back to Tickets
      </button>

      <h2 className="text-xl font-bold text-slate-900 mb-1">New Support Ticket</h2>
      <p className="text-xs text-slate-500 mb-5">Configure each issue, then submit as a batch.</p>

      {/* Completed Issues Stack */}
      {completedIssues.length > 0 && (
        <div className="space-y-3 mb-5">
          <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Completed Issues ({completedIssues.length})</h3>
          {completedIssues.map((issue, idx) => (
            <div key={issue.id} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-slate-500">Issue #{idx + 1}</span>
                    <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-semibold border-2 border-indigo-600 bg-indigo-50 text-indigo-700")}>
                      {ISSUE_CATEGORY_LABELS[issue.config.category]}
                    </span>
                    <span className="text-[10px] text-slate-400">{ISSUE_MODULE_LABELS[issue.config.module]}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-1">
                    {brandNames(issue.config.brandIds).map(name => (
                      <span key={name} className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-[10px] font-medium">{name}</span>
                    ))}
                    {issue.config.aggregators.map(a => (
                      <span key={a} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-[10px] font-medium">{a}</span>
                    ))}
                    {issue.config.manualAgg && <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-[10px] font-medium">{issue.config.manualAgg}</span>}
                  </div>
                  <p className="text-xs text-slate-600 line-clamp-2">{issue.details}</p>
                  {issue.attachments.length > 0 && <div className="flex items-center gap-1 mt-1"><Paperclip size={10} className="text-slate-400" /><span className="text-[10px] text-slate-400">{issue.attachments.length} file(s)</span></div>}
                </div>
                <div className="flex items-center gap-1 ml-3">
                  <button onClick={() => editCompletedIssue(issue.id)} className="p-1.5 text-slate-400 hover:text-indigo-600 transition-colors" title="Edit"><Pencil size={14} /></button>
                  <button onClick={() => removeCompletedIssue(issue.id)} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors" title="Remove"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stage 1: Configuration Panel (Animated Slide) */}
      {!configApplied && (
        <div className={cn("bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-5",
          configAnimating === 'opening' ? "animate-config-slide-down" : configAnimating === 'closing' ? "animate-config-slide-up" : "animate-config-slide-down")}>
          <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
            <h3 className="text-sm font-bold text-slate-800">Configure Issue #{completedIssues.length + 1}</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Select area, type, brands, and aggregators using the chips below.</p>
          </div>
          <div className="p-6 space-y-5">
            {/* Area of Issue — Chip Selection */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Area of Issue</label>
              <div className="flex flex-wrap gap-2">
                {Object.values(IssueModule).filter(m => availableModules.includes(m)).map(mod => (
                  <button key={mod} onClick={() => setConfig(prev => ({ ...prev, module: mod }))}
                    className={cn("px-4 py-2 rounded-xl text-xs font-semibold border-2 transition-all",
                      config.module === mod
                        ? "border-indigo-600 bg-indigo-50 text-indigo-700 shadow-sm"
                        : "border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50")}>
                    {ISSUE_MODULE_LABELS[mod]}
                  </button>
                ))}
              </div>
            </div>

            {/* Issue Type — Chip Grid */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Issue Type</label>
              <div className="flex flex-wrap gap-2">
                {Object.values(IssueCategory).map(cat => (
                  <button key={cat} onClick={() => setConfig(prev => ({ ...prev, category: cat }))}
                    className={cn("flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 transition-all text-xs font-semibold",
                      config.category === cat
                        ? "border-indigo-600 bg-indigo-50 text-indigo-700 shadow-sm"
                        : "border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50")}>
                    <CategoryIcon name={ISSUE_CATEGORY_ICONS[cat]} className={cn("w-3.5 h-3.5",
                      config.category === cat ? "text-indigo-600" : "text-slate-400")} />
                    {ISSUE_CATEGORY_LABELS[cat]}
                  </button>
                ))}
              </div>
            </div>

            {/* Brands — Chip Multi-Select */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Brand(s)</label>
              <div className="flex flex-wrap gap-2">
                {brands.map(b => {
                  const selected = config.brandIds.includes(b.id);
                  return (
                    <button key={b.id} onClick={() => setConfig(prev => ({
                      ...prev, brandIds: selected ? prev.brandIds.filter(id => id !== b.id) : [...prev.brandIds, b.id]
                    }))} className={cn("px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all",
                      selected
                        ? "border-indigo-600 bg-indigo-50 text-indigo-700 shadow-sm"
                        : "border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50")}>
                      {b.name}
                    </button>
                  );
                })}
                {brands.length === 0 && <span className="text-xs text-slate-400 italic">No brands available</span>}
              </div>
            </div>

            {/* Aggregators — Dynamic Smart Chips */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Aggregator(s)
                {aggAccess && linkedAggs.length > 0 && config.brandIds.length > 0 && (
                  <span className="ml-2 text-[10px] text-emerald-600 font-normal normal-case">&bull; Auto-detected from brand campaigns</span>
                )}
              </label>
              {aggLoading ? (
                <div className="text-xs text-slate-400 py-2">Detecting linked aggregators...</div>
              ) : !aggAccess && config.brandIds.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs text-amber-600">No linked aggregators found. Enter manually:</p>
                  <input value={config.manualAgg} onChange={e => setConfig(prev => ({ ...prev, manualAgg: e.target.value }))}
                    placeholder="e.g. Talabat" className="px-3 py-2 border border-slate-200 rounded-xl text-sm w-64 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {aggChips.map(agg => {
                    const selected = config.aggregators.includes(agg);
                    return (
                      <button key={agg} onClick={() => setConfig(prev => ({
                        ...prev, aggregators: selected ? prev.aggregators.filter(a => a !== agg) : [...prev.aggregators, agg]
                      }))} className={cn("px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all",
                        selected
                          ? "border-indigo-600 bg-indigo-50 text-indigo-700 shadow-sm"
                          : "border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50")}>
                        {agg}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Apply Configuration Button */}
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <div className="text-[10px] text-slate-400">
                {!configValid && 'Select a brand and issue type to continue'}
              </div>
              <button onClick={applyConfig} disabled={!configValid}
                className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm">
                <Check size={16} /> Apply Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stage 2: Sticky Summary + Workspace */}
      {configApplied && (
        <>
          {/* Sticky Summary Card */}
          <div className="sticky top-0 z-50 bg-white border border-slate-200 rounded-2xl shadow-md p-4 mb-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-wrap min-w-0">
                <span className="text-xs font-bold text-slate-500">Issue #{completedIssues.length + 1}</span>
                <span className={cn("px-2.5 py-0.5 rounded-full text-[10px] font-semibold border-2 border-indigo-600 bg-indigo-50 text-indigo-700")}>
                  {ISSUE_CATEGORY_LABELS[config.category]}
                </span>
                <span className="text-[10px] text-slate-400">{ISSUE_MODULE_LABELS[config.module]}</span>
                <span className="text-slate-300">|</span>
                {brandNames(config.brandIds).map(name => (
                  <span key={name} className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-[10px] font-medium">{name}</span>
                ))}
                {config.aggregators.map(a => (
                  <span key={a} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-[10px] font-medium">{a}</span>
                ))}
                {config.manualAgg && !config.aggregators.length && (
                  <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-[10px] font-medium">{config.manualAgg}</span>
                )}
              </div>
              <button onClick={changeConfig} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors shrink-0">
                <Pencil size={13} /> Change
              </button>
            </div>
          </div>

          {/* Workspace: Description + Upload (scrollable below sticky) */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 space-y-5 mb-5">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Description</label>
              <textarea value={details} onChange={e => setDetails(e.target.value)}
                placeholder="Describe the issue in detail..."
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 resize-none transition-all h-32" />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Attachments</label>
              <div onDragOver={e => e.preventDefault()} onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center justify-center gap-1.5 px-4 py-5 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/20 transition-all">
                <Upload size={20} className="text-slate-400" />
                <span className="text-xs text-slate-500">Drop files or click to upload</span>
                <span className="text-[10px] text-slate-400">Max 5MB per file</span>
              </div>
              <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" onChange={handleFileUpload} className="hidden" />
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {attachments.map(att => (
                    <div key={att.id} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
                      <FileText size={12} className="text-slate-400" />
                      <span className="max-w-[120px] truncate">{att.name}</span>
                      <button onClick={e => { e.stopPropagation(); const f = stagedFilesRef.current.get(att.id); if (f) { URL.revokeObjectURL(att.data); stagedFilesRef.current.delete(att.id); } setAttachments(prev => prev.filter(a => a.id !== att.id)); }} className="text-slate-400 hover:text-red-500"><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button onClick={completeCurrentIssue} disabled={!details.trim()}
              className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-slate-300 text-slate-500 rounded-xl text-sm font-semibold hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
              <Plus size={16} /> Save & Add Another Issue
            </button>
            <div className="flex-1" />
            <div className="text-xs text-slate-400">{completedIssues.length + (details.trim() ? 1 : 0)} issue(s) total</div>
            <button onClick={handleSubmit} disabled={!allIssuesReady || submitting}
              className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm">
              <Send size={16} /> {submitting ? 'Uploading…' : 'Submit Ticket'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// --- Resolution Workspace (Admin Mirroring — Sticky Summary) ------------------

interface ResolutionProps {
  ticket: Ticket;
  user: User | null;
  brands: Brand[];
  onBack: () => void;
  onUpdateTicket: (updated: Ticket) => void;
}

function ResolutionWorkspace({ ticket, user, brands, onBack, onUpdateTicket }: ResolutionProps) {
  const isAdmin = hasTabVariant(user, 'support-desk', 'ticket-center', 'admin');
  const isManager = !isAdmin;
  const resNav = useNavigate();
  const [chatInput, setChatInput] = useState('');
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [activeIssueIdx, setActiveIssueIdx] = useState(0);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const resConfig = useMemo(() => loadDeskConfig(), []);

  // Resolution Settings modal state (Admin)
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');
  const [autoCloseHours, setAutoCloseHours] = useState<number>(() => resConfig.autoCloseDurations[1]?.hours ?? resConfig.autoCloseDurations[0]?.hours ?? 48);
  const [resolvingScope, setResolvingScope] = useState<'single' | 'all'>('single');
  const [resolvingIssueId, setResolvingIssueId] = useState<string | null>(null);

  // Manager dispute state
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeMessage, setDisputeMessage] = useState('');

  const [rejectingIssueId, setRejectingIssueId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const MENTION_TARGETS = ['@aggregator-module', '@inventory-module', '@rider-module', '@support-admin', '@store-manager', '@brand-team'];

  useEffect(() => {
    if (chatContainerRef.current) chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
  }, [ticket.messages, activeIssueIdx]);

  const handleChatInput = (val: string) => {
    setChatInput(val);
    const atMatch = val.match(/@(\w*)$/);
    if (atMatch) { setMentionQuery(atMatch[1]); setShowMentions(true); }
    else setShowMentions(false);
  };

  const insertMention = (target: string) => {
    setChatInput(prev => prev.replace(/@\w*$/, target + ' '));
    setShowMentions(false);
  };

  /** Change ticket status via the admin status switcher */
  const changeTicketStatus = (newStatus: TicketStatus) => {
    if (!isAdmin || newStatus === ticket.status) return;
    const now = Date.now();
    const historyEntry: StatusHistoryEntry = {
      status: newStatus, timestamp: now,
      changedBy: user?.id ?? 'system', changedByName: user?.name ?? 'System',
    };
    const sla = { ...ticket.sla };
    if (newStatus === TicketStatus.OPEN && !sla.firstResponseAt) sla.firstResponseAt = now;
    if (newStatus === TicketStatus.RESOLVED && !sla.resolvedAt) sla.resolvedAt = now;
    onUpdateTicket({
      ...ticket, status: newStatus,
      statusHistory: [...(ticket.statusHistory ?? []), historyEntry],
      sla, updatedAt: now,
    });
  };

  const sendMessage = () => {
    if (!chatInput.trim() || !user || !activeReport) return;
    const now = Date.now();
    const msg: TicketMessage = { id: crypto.randomUUID(), ticketId: ticket.id, issueReportId: activeReport.id, senderId: user.id, senderName: user.name, senderRole: isAdmin ? 'admin' : 'manager', content: chatInput.trim(), createdAt: now };
    // Auto-move NEW → OPEN on first admin reply
    let nextStatus = ticket.status;
    let nextHistory = ticket.statusHistory ?? [];
    const nextSla = { ...ticket.sla };
    if (isAdmin && ticket.status === TicketStatus.NEW) {
      nextStatus = TicketStatus.OPEN;
      nextHistory = [...nextHistory, { status: TicketStatus.OPEN, timestamp: now, changedBy: user.id, changedByName: user.name }];
      if (!nextSla.firstResponseAt) nextSla.firstResponseAt = now;
    }
    onUpdateTicket({ ...ticket, status: nextStatus, statusHistory: nextHistory, sla: nextSla, messages: [...ticket.messages, msg], updatedAt: now });
    setChatInput('');
  };

  const resolveIssue = (reportId: string, newStatus: IssueReportStatus) => {
    if (!isAdmin) return;
    const now = Date.now();
    const updatedReports = ticket.issueReports.map(r => r.id === reportId ? { ...r, status: newStatus } : r);
    const report = updatedReports.find(r => r.id === reportId)!;
    const autoMsg: TicketMessage = { id: crypto.randomUUID(), ticketId: ticket.id, issueReportId: reportId, senderId: user?.id ?? 'system', senderName: user?.name ?? 'System', senderRole: 'admin', content: `Issue "${ISSUE_CATEGORY_LABELS[report.category]}" marked as ${newStatus}.`, createdAt: now };
    const derivedStatus = deriveTicketStatus(updatedReports, ticket.status);
    const sla = { ...ticket.sla };
    if (derivedStatus === TicketStatus.RESOLVED && !sla.resolvedAt) sla.resolvedAt = now;
    // Track status change in history if derived status changes
    let nextHistory = ticket.statusHistory ?? [];
    if (derivedStatus !== ticket.status) {
      nextHistory = [...nextHistory, { status: derivedStatus, timestamp: now, changedBy: user?.id ?? 'system', changedByName: user?.name ?? 'System' }];
    }
    onUpdateTicket({ ...ticket, issueReports: updatedReports, status: derivedStatus, statusHistory: nextHistory, sla, messages: [...ticket.messages, autoMsg], updatedAt: now });
  };

  /** Opens the Resolution Settings modal for a single issue */
  const openResolveModal = (reportId: string) => {
    setResolvingIssueId(reportId);
    setResolvingScope('single');
    setResolutionNote('');
    setAutoCloseHours(resConfig.autoCloseDurations[1]?.hours ?? resConfig.autoCloseDurations[0]?.hours ?? 48);
    setShowResolutionModal(true);
  };

  /** Opens the Resolution Settings modal for all pending issues (bulk) */
  const openBulkResolveModal = () => {
    setResolvingIssueId(null);
    setResolvingScope('all');
    setResolutionNote('');
    setAutoCloseHours(resConfig.autoCloseDurations[1]?.hours ?? resConfig.autoCloseDurations[0]?.hours ?? 48);
    setShowResolutionModal(true);
  };

  /** Confirm resolution from modal — resolves issues, sets ticket to RESOLVED with auto-close timer */
  const confirmResolution = () => {
    if (!isAdmin || !user) return;
    const now = Date.now();
    const expiry = now + autoCloseHours * 60 * 60 * 1000;
    // Resolve the target issues
    let updatedReports = ticket.issueReports;
    if (resolvingScope === 'single' && resolvingIssueId) {
      updatedReports = updatedReports.map(r => r.id === resolvingIssueId ? { ...r, status: IssueReportStatus.RESOLVED } : r);
    } else {
      updatedReports = updatedReports.map(r => r.status === IssueReportStatus.PENDING ? { ...r, status: IssueReportStatus.RESOLVED } : r);
    }
    const derivedStatus = deriveTicketStatus(updatedReports, ticket.status);
    const sla = { ...ticket.sla };
    if (derivedStatus === TicketStatus.RESOLVED && !sla.resolvedAt) sla.resolvedAt = now;
    const resRecord: ResolutionRecord = {
      resolvedBy: user.id, resolvedByName: user.name, resolvedAt: now,
      autoCloseHours, resolutionNote: resolutionNote.trim(), autoCloseExpiry: expiry,
      managerVerdict: null,
    };
    const autoMsg: TicketMessage = {
      id: crypto.randomUUID(), ticketId: ticket.id, senderId: user.id, senderName: user.name, senderRole: 'admin',
      content: `✅ ${resolvingScope === 'all' ? 'All issues' : 'Issue'} marked as RESOLVED.\n📝 ${resolutionNote.trim() || 'No note provided.'}\n⏱️ Auto-close in ${autoCloseHours}h if no response.`,
      createdAt: now,
    };
    let nextHistory = ticket.statusHistory ?? [];
    if (derivedStatus !== ticket.status) {
      nextHistory = [...nextHistory, { status: derivedStatus, timestamp: now, changedBy: user.id, changedByName: user.name }];
    }
    onUpdateTicket({
      ...ticket, issueReports: updatedReports, status: derivedStatus,
      statusHistory: nextHistory, sla, messages: [...ticket.messages, autoMsg],
      updatedAt: now, autoCloseExpiry: expiry,
      resolutionHistory: [...(ticket.resolutionHistory ?? []), resRecord],
    });
    setShowResolutionModal(false);
    setResolutionNote('');
  };

  /** Manager confirms the resolution → ticket moves to CLOSED */
  const managerConfirmClose = () => {
    if (!user) return;
    const now = Date.now();
    const updatedHistory = (ticket.resolutionHistory ?? []).map((r, i, arr) =>
      i === arr.length - 1 ? { ...r, managerVerdict: 'confirmed' as const, verdictAt: now } : r
    );
    const statusEntry: StatusHistoryEntry = { status: TicketStatus.CLOSED, timestamp: now, changedBy: user.id, changedByName: user.name };
    const autoMsg: TicketMessage = {
      id: crypto.randomUUID(), ticketId: ticket.id, senderId: user.id, senderName: user.name, senderRole: 'manager',
      content: '✅ Manager confirmed the resolution. Ticket closed.', createdAt: now,
    };
    onUpdateTicket({
      ...ticket, status: TicketStatus.CLOSED,
      statusHistory: [...(ticket.statusHistory ?? []), statusEntry],
      messages: [...ticket.messages, autoMsg],
      updatedAt: now, autoCloseExpiry: undefined,
      resolutionHistory: updatedHistory,
    });
  };

  /** Manager disputes the resolution → ticket reopens to OPEN */
  const managerDispute = () => {
    if (!user || !disputeMessage.trim()) return;
    const now = Date.now();
    const updatedHistory = (ticket.resolutionHistory ?? []).map((r, i, arr) =>
      i === arr.length - 1 ? { ...r, managerVerdict: 'disputed' as const, verdictAt: now, verdictMessage: disputeMessage.trim() } : r
    );
    // Reopen all resolved issues to PENDING
    const updatedReports = ticket.issueReports.map(r => r.status === IssueReportStatus.RESOLVED ? { ...r, status: IssueReportStatus.PENDING } : r);
    const statusEntry: StatusHistoryEntry = { status: TicketStatus.OPEN, timestamp: now, changedBy: user.id, changedByName: user.name };
    const autoMsg: TicketMessage = {
      id: crypto.randomUUID(), ticketId: ticket.id, senderId: user.id, senderName: user.name, senderRole: 'manager',
      content: `🔴 Resolution disputed by manager.\nReason: ${disputeMessage.trim()}`, createdAt: now,
    };
    onUpdateTicket({
      ...ticket, status: TicketStatus.OPEN, issueReports: updatedReports,
      statusHistory: [...(ticket.statusHistory ?? []), statusEntry],
      messages: [...ticket.messages, autoMsg],
      updatedAt: now, autoCloseExpiry: undefined,
      resolutionHistory: updatedHistory,
    });
    setShowDisputeModal(false);
    setDisputeMessage('');
  };

  const filteredMentions = MENTION_TARGETS.filter(t => t.includes(mentionQuery.toLowerCase()));
  const activeReport = ticket.issueReports[activeIssueIdx];
  const brandNames = (ids: string[]) => ids.map(id => brands.find(b => b.id === id)?.name ?? id);
  const issueMessages = activeReport ? ticket.messages.filter(m => !m.issueReportId || m.issueReportId === activeReport.id) : ticket.messages;
  const stageInfo = calculateTimeInStage(ticket);

  return (
    <div className="px-6 pb-8">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-5 transition-colors">
        <ArrowLeft size={16} /> Back to Tickets
      </button>

      {/* Ticket Meta Header */}
      <div className="flex items-center gap-3 mb-2">
        <span className="text-xs font-mono text-slate-400">{ticket.id}</span>
        <span className="text-xs text-slate-400">by {ticket.managerName} &middot; {new Date(ticket.createdAt).toLocaleDateString()}</span>
        {stageInfo && (
          <span className="text-[10px] text-slate-400 flex items-center gap-1">
            <Clock size={11} /> {formatDuration(stageInfo.durationMs)} in stage
          </span>
        )}
        <div className="ml-auto">
          <span className="text-xs text-slate-500">
            {ticket.issueReports.filter(r => r.status === IssueReportStatus.RESOLVED).length}/{ticket.issueReports.length} resolved
          </span>
        </div>
      </div>

      {/* ── Status Transition Rail ─────────────────────────────────────── */}
      {(() => {
        const RAIL_STATUSES: TicketStatus[] = [TicketStatus.NEW, TicketStatus.OPEN, TicketStatus.IN_PROGRESS, TicketStatus.PENDING];
        const RAIL_COLORS: Record<string, { active: string; completed: string; icon: string }> = {
          [TicketStatus.NEW]:         { active: 'bg-sky-500 text-white shadow-sky-200', completed: 'bg-sky-100 text-sky-700', icon: 'text-sky-600' },
          [TicketStatus.OPEN]:        { active: 'bg-blue-600 text-white shadow-blue-200', completed: 'bg-blue-100 text-blue-700', icon: 'text-blue-600' },
          [TicketStatus.IN_PROGRESS]: { active: 'bg-indigo-600 text-white shadow-indigo-200', completed: 'bg-indigo-100 text-indigo-700', icon: 'text-indigo-600' },
          [TicketStatus.PENDING]:     { active: 'bg-orange-500 text-white shadow-orange-200', completed: 'bg-orange-100 text-orange-700', icon: 'text-orange-600' },
        };
        const currentIdx = RAIL_STATUSES.indexOf(ticket.status);
        const isTerminal = ticket.status === TicketStatus.RESOLVED || ticket.status === TicketStatus.CLOSED;

        return (
          <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-4 shadow-sm">
            {isTerminal ? (
              <div className="flex items-center justify-center gap-3 py-2">
                <div className={cn("w-8 h-8 rounded-full flex items-center justify-center",
                  ticket.status === TicketStatus.RESOLVED ? "bg-emerald-500" : "bg-slate-400")}>
                  {ticket.status === TicketStatus.RESOLVED ? <CheckCircle2 size={16} className="text-white" /> : <Ban size={16} className="text-white" />}
                </div>
                <div className="text-center">
                  <span className={cn("text-sm font-bold uppercase tracking-wide block",
                    ticket.status === TicketStatus.RESOLVED ? "text-emerald-700" : "text-slate-500")}>
                    Ticket {TICKET_STATUS_LABELS[ticket.status]}
                  </span>
                  {ticket.status === TicketStatus.RESOLVED && ticket.autoCloseExpiry && (
                    <span className="text-[10px] text-amber-600 flex items-center gap-1 justify-center mt-1">
                      <Hourglass size={10} className="animate-pulse" />
                      Auto-close in {formatDuration(Math.max(0, ticket.autoCloseExpiry - Date.now()))}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                {RAIL_STATUSES.map((status, idx) => {
                  const colors = RAIL_COLORS[status];
                  const isCurrent = status === ticket.status;
                  const isCompleted = !isTerminal && currentIdx >= 0 && idx < currentIdx;
                  const isFuture = !isTerminal && currentIdx >= 0 && idx > currentIdx;
                  const isClickable = isAdmin && !isTerminal;

                  return (
                    <React.Fragment key={status}>
                      <button
                        onClick={() => isClickable && changeTicketStatus(status)}
                        disabled={!isClickable}
                        className={cn(
                          "flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex-1 justify-center relative",
                          isCurrent && `${colors.active} shadow-lg`,
                          isCompleted && `${colors.completed}`,
                          isFuture && "bg-slate-50 text-slate-400 border border-slate-200",
                          isClickable && !isCurrent && "hover:scale-[1.02] cursor-pointer",
                          !isClickable && "cursor-default",
                        )}
                      >
                        {isCompleted && <Check size={12} className="shrink-0" />}
                        {isCurrent && <div className="w-2 h-2 rounded-full bg-white animate-pulse shrink-0" />}
                        <span className="whitespace-nowrap">{TICKET_STATUS_LABELS[status]}</span>
                      </button>
                      {idx < RAIL_STATUSES.length - 1 && (
                        <ChevronRight size={14} className={cn("shrink-0", idx < currentIdx ? "text-slate-400" : "text-slate-200")} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Manager Verdict Banner — shown when ticket is RESOLVED ──── */}
      {ticket.status === TicketStatus.RESOLVED && isManager && (
        <div className="bg-gradient-to-r from-amber-50 via-amber-100/60 to-amber-50 border-2 border-amber-300 rounded-2xl p-5 mb-4 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center shrink-0">
              <ShieldCheck size={20} className="text-amber-700" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-amber-900">Admin has marked this issue as resolved. Please verify the fix.</h3>
              {ticket.resolutionHistory && ticket.resolutionHistory.length > 0 && (
                <p className="text-xs text-amber-700 mt-1 italic">
                  "{ticket.resolutionHistory[ticket.resolutionHistory.length - 1].resolutionNote || 'No resolution note provided.'}"
                </p>
              )}
              {ticket.autoCloseExpiry && (
                <p className="text-[10px] text-amber-600 mt-2 flex items-center gap-1">
                  <Hourglass size={10} /> This ticket will auto-close in {formatDuration(Math.max(0, ticket.autoCloseExpiry - Date.now()))} if no action is taken.
                </p>
              )}
              <div className="flex gap-3 mt-4">
                <button onClick={managerConfirmClose}
                  className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 transition-all shadow-sm hover:shadow-md">
                  <CheckCircle2 size={14} /> Confirm &amp; Close
                </button>
                <button onClick={() => setShowDisputeModal(true)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-white text-red-600 border-2 border-red-200 rounded-xl text-xs font-bold hover:bg-red-50 transition-all">
                  <XCircle size={14} /> Still Have Issues
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Issue Tab Bar */}
      {ticket.issueReports.length > 1 && (
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {ticket.issueReports.map((report, idx) => (
            <button key={report.id} onClick={() => setActiveIssueIdx(idx)}
              className={cn("flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all whitespace-nowrap",
                activeIssueIdx === idx
                  ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                  : "border-slate-200 text-slate-500 hover:border-slate-300")}>
              <CategoryIcon name={ISSUE_CATEGORY_ICONS[report.category]} className={cn("w-3.5 h-3.5", activeIssueIdx === idx ? "text-indigo-600" : "text-slate-400")} />
              Issue #{idx + 1}
              <span className={cn("px-1.5 py-0.5 rounded-full text-[9px] font-semibold ml-1",
                report.status === IssueReportStatus.RESOLVED ? "bg-emerald-100 text-emerald-700" :
                report.status === IssueReportStatus.REJECTED ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500")}>{report.status}</span>
            </button>
          ))}
        </div>
      )}

      {/* Main Grid — 3/5 Issue Detail + 2/5 Chat */}
      <div className="grid grid-cols-5 gap-6">

        {/* Left Column — Active Issue Detail */}
        <div className="col-span-3 space-y-5">

          {/* Sticky Summary Card — Admin Mirroring + Action Bar */}
          {activeReport && (
            <div className={cn("sticky top-0 z-50 rounded-2xl border shadow-md overflow-hidden transition-all",
              activeReport.status === IssueReportStatus.RESOLVED ? "bg-emerald-50/50 border-emerald-200" :
              activeReport.status === IssueReportStatus.REJECTED ? "bg-slate-50 border-slate-300 opacity-80" :
              "bg-gradient-to-r from-white via-slate-50 to-white border-slate-200")}>

              {/* Action Header Bar */}
              <div className={cn("flex items-center justify-between px-4 py-2.5 border-b",
                activeReport.status === IssueReportStatus.RESOLVED ? "bg-emerald-100/60 border-emerald-200" :
                activeReport.status === IssueReportStatus.REJECTED ? "bg-slate-100 border-slate-300" :
                "bg-slate-50 border-slate-200")}>
                <div className="flex items-center gap-2">
                  <CategoryIcon name={ISSUE_CATEGORY_ICONS[activeReport.category]} className={cn("w-4 h-4",
                    activeReport.status === IssueReportStatus.RESOLVED ? "text-emerald-600" :
                    activeReport.status === IssueReportStatus.REJECTED ? "text-slate-400" : "text-indigo-600")} />
                  <span className="text-sm font-bold text-slate-800">{ISSUE_CATEGORY_LABELS[activeReport.category]}</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">{ISSUE_MODULE_LABELS[activeReport.module]}</span>
                </div>
                <div className="flex items-center gap-2">
                  {/* Admin Compact Actions — only when issue is still PENDING */}
                  {isAdmin && activeReport.status === IssueReportStatus.PENDING && (
                    <>
                      <button onClick={() => openResolveModal(activeReport.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-[11px] font-bold hover:bg-emerald-700 transition-all shadow-sm hover:shadow-md">
                        <CheckCircle2 size={13} /> Resolve
                      </button>
                      <button onClick={() => setRejectingIssueId(activeReport.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-red-600 border border-red-200 rounded-lg text-[11px] font-bold hover:bg-red-50 transition-all">
                        <XCircle size={13} /> Reject
                      </button>
                    </>
                  )}
                  {/* Status badge for resolved/rejected */}
                  {activeReport.status !== IssueReportStatus.PENDING && (
                    <span className={cn("px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider",
                      activeReport.status === IssueReportStatus.RESOLVED ? "bg-emerald-200 text-emerald-800" : "bg-red-200 text-red-800")}>
                      {activeReport.status === IssueReportStatus.RESOLVED ? '✓ Resolved' : '✕ Rejected'}
                    </span>
                  )}
                  {/* Go to Issue Location — navigate to the relevant module */}
                  <button
                    onClick={() => {
                      const appSlug = ISSUE_MODULE_REQUIRED_APP[activeReport.module];
                      if (appSlug) {
                        // Store floating ticket context for side-car
                        try { sessionStorage.setItem('support_sidecar', JSON.stringify({ ticketId: ticket.id, ticketRef: ticket.subject, issueCategory: activeReport.category, module: activeReport.module })); } catch {}
                        resNav(`/apps/${appSlug}`);
                      }
                    }}
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-600 text-white rounded-lg text-[10px] font-bold hover:bg-indigo-700 transition-all shadow-sm"
                    title="Navigate to the module related to this issue"
                  >
                    <ExternalLink size={11} /> Go to Issue
                  </button>
                </div>
              </div>

              {/* Summary content */}
              <div className="p-4 relative">
                {/* Watermark stamp overlay */}
                {activeReport.status === IssueReportStatus.RESOLVED && (
                  <div className="absolute top-2 right-4 pointer-events-none">
                    <div className="text-emerald-200 font-black text-3xl uppercase tracking-widest rotate-[-12deg] border-4 border-emerald-200 rounded-xl px-4 py-1 opacity-60">
                      Fixed
                    </div>
                  </div>
                )}
                {activeReport.status === IssueReportStatus.REJECTED && (
                  <div className="absolute top-2 right-4 pointer-events-none">
                    <div className="text-red-200 font-black text-3xl uppercase tracking-widest rotate-[-12deg] border-4 border-red-200 rounded-xl px-4 py-1 opacity-60">
                      Rejected
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                  {activeReport.brandIds.length > 0 && (
                    <div className="flex items-center gap-1">
                      <Building2 size={12} />
                      {brandNames(activeReport.brandIds).map(name => (
                        <span key={name} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]">{name}</span>
                      ))}
                    </div>
                  )}
                  {activeReport.aggregators.length > 0 && (
                    <div className="flex items-center gap-1">
                      <BarChart3 size={12} />
                      {activeReport.aggregators.map(a => (
                        <span key={a} className={cn("px-1.5 py-0.5 rounded text-[10px] font-medium", AGGREGATOR_COLORS[a] ?? "bg-slate-100 text-slate-600")}>{a}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Issue Details Card — Clean, no action buttons in body */}
          {activeReport && (
            <div className={cn("rounded-2xl border p-5 transition-all",
              activeReport.status === IssueReportStatus.RESOLVED ? "bg-emerald-50/30 border-emerald-200" :
              activeReport.status === IssueReportStatus.REJECTED ? "bg-slate-50 border-slate-300 opacity-75" :
              "bg-white border-slate-200")}>
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Issue Details</h4>
              <p className={cn("text-sm whitespace-pre-wrap leading-relaxed",
                activeReport.status === IssueReportStatus.REJECTED ? "text-slate-400" : "text-slate-600")}>{activeReport.details}</p>

              {activeReport.attachments.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <h5 className="text-xs font-semibold text-slate-500 mb-2">Attachments</h5>
                  <div className="grid grid-cols-3 gap-3">
                    {activeReport.attachments.map(att => {
                      const isImage = att.type.startsWith('image/');
                      const src = isImage ? (att.data.startsWith('http') || att.data.startsWith('data:') ? att.data : att.data.startsWith('/api/') ? getMediaUrl(att.data) ?? '' : `data:${att.type};base64,${att.data}`) : '';
                      return isImage ? (
                        <div key={att.id} className="rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
                          <img src={src} alt={att.name} className="w-full h-32 object-cover" />
                          <div className="px-2 py-1.5 text-[10px] text-slate-500 truncate">{att.name}</div>
                        </div>
                      ) : (
                        <div key={att.id} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-100 text-xs text-slate-600">
                          <FileText size={12} /> {att.name} <span className="text-slate-400">({formatFileSize(att.size)})</span>
                        </div>
                      );
                    })}
                  </div>
                  {activeReport.attachments.filter(a => a.type.startsWith('image/')).length === 0 && activeReport.attachments.length > 0 && null}
                </div>
              )}
              {activeReport.attachments.length === 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Image size={14} /> No images attached
                  </div>
                </div>
              )}

              {/* Resolution note display */}
              {activeReport.status === IssueReportStatus.REJECTED && activeReport.resolutionNote && (
                <div className="mt-4 pt-3 border-t border-red-100">
                  <h5 className="text-xs font-semibold text-red-500 mb-1">Rejection Reason</h5>
                  <p className="text-xs text-red-600 italic">{activeReport.resolutionNote}</p>
                </div>
              )}
            </div>
          )}

          {/* SLA Card */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h4 className="text-sm font-semibold text-slate-700 mb-3">SLA Tracking</h4>
            <div className="grid grid-cols-3 gap-4 text-xs">
              <div><span className="text-slate-400">First Response</span><div className="font-semibold text-slate-700 mt-1">{ticket.sla.firstResponseAt ? formatDuration(ticket.sla.firstResponseAt - ticket.createdAt) : '—'}</div></div>
              <div><span className="text-slate-400">Resolution</span><div className="font-semibold text-slate-700 mt-1">{ticket.sla.resolvedAt ? formatDuration(ticket.sla.resolvedAt - ticket.createdAt) : 'In progress'}</div></div>
              <div><span className="text-slate-400">Created</span><div className="font-semibold text-slate-700 mt-1">{formatDate(ticket.createdAt)}</div></div>
            </div>
            {ticket.autoCloseExpiry && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 text-xs">
                <Hourglass size={12} className="text-amber-500" />
                <span className="text-slate-500">Auto-Close Timer:</span>
                <span className={cn("font-bold", ticket.autoCloseExpiry > Date.now() ? "text-amber-600" : "text-red-600")}>
                  {ticket.autoCloseExpiry > Date.now()
                    ? `${formatDuration(ticket.autoCloseExpiry - Date.now())} remaining`
                    : 'Expired'}
                </span>
                <span className="text-slate-400 text-[10px]">({formatDate(ticket.autoCloseExpiry)})</span>
              </div>
            )}
          </div>

          {/* Resolution History */}
          {ticket.resolutionHistory && ticket.resolutionHistory.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Activity size={14} className="text-indigo-600" /> Resolution History</h4>
              <div className="space-y-3">
                {ticket.resolutionHistory.map((rec, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-xl p-3 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-slate-700">Resolved by {rec.resolvedByName}</span>
                      <span className="text-slate-400">{formatDate(rec.resolvedAt)}</span>
                    </div>
                    {rec.resolutionNote && <p className="text-slate-600 italic mb-1">"{rec.resolutionNote}"</p>}
                    <div className="flex items-center gap-3 text-[10px] text-slate-500">
                      <span>Auto-close: {rec.autoCloseHours}h</span>
                      <span className={cn("font-bold",
                        rec.managerVerdict === 'confirmed' ? "text-emerald-600" :
                        rec.managerVerdict === 'disputed' ? "text-red-600" :
                        rec.autoClosedBySystem ? "text-amber-600" : "text-slate-400")}>
                        {rec.managerVerdict === 'confirmed' ? '✓ Manager Confirmed' :
                         rec.managerVerdict === 'disputed' ? '✕ Manager Disputed' :
                         rec.autoClosedBySystem ? '🔒 Auto-Closed by System' :
                         '⏳ Awaiting Verdict'}
                      </span>
                    </div>
                    {rec.verdictMessage && <p className="text-red-500 mt-1 text-[10px]">Dispute: "{rec.verdictMessage}"</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Resolve All Issues — Bulk Action ───────────────────────── */}
          {isAdmin && ticket.issueReports.length > 1 && ticket.issueReports.some(r => r.status === IssueReportStatus.PENDING) && (
            <div className="bg-gradient-to-r from-emerald-50 to-emerald-100/50 border border-emerald-200 rounded-2xl p-4 flex items-center justify-between">
              <div>
                <h4 className="text-sm font-bold text-emerald-800">Bulk Resolution</h4>
                <p className="text-[11px] text-emerald-600 mt-0.5">
                  {ticket.issueReports.filter(r => r.status === IssueReportStatus.PENDING).length} of {ticket.issueReports.length} issues still pending
                </p>
              </div>
              <button
                onClick={openBulkResolveModal}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 transition-all shadow-sm hover:shadow-lg"
              >
                <CheckCircle2 size={14} /> Resolve All Issues
              </button>
            </div>
          )}
        </div>

        {/* Right Column — Per-Issue Chat */}
        <div className="col-span-2 bg-white rounded-2xl border border-slate-200 flex flex-col" style={{ maxHeight: 'calc(100vh - 160px)' }}>
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-slate-700">Issue #{activeIssueIdx + 1} Chat</h4>
              {activeReport && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">{ISSUE_CATEGORY_LABELS[activeReport.category]}</span>}
            </div>
            <p className="text-[10px] text-slate-400 mt-0.5">{issueMessages.length} messages &middot; Use @mention to tag teams</p>
          </div>

          <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {issueMessages.map(msg => (
              <div key={msg.id} className={cn("max-w-[85%] rounded-2xl px-4 py-2.5", msg.senderRole === 'admin' ? "bg-indigo-600 text-white ml-auto" : "bg-slate-100 text-slate-700")}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-semibold opacity-80">{msg.senderName}</span>
                  <span className="text-[9px] opacity-50">{timeAgo(msg.createdAt)}</span>
                </div>
                <p className="text-xs leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              </div>
            ))}
            {issueMessages.length === 0 && (
              <div className="text-center py-10 text-xs text-slate-400">No messages for this issue yet. Start the conversation.</div>
            )}
          </div>

          <div className="p-3 border-t border-slate-100 relative">
            {showMentions && filteredMentions.length > 0 && (
              <div className="absolute bottom-full left-3 right-3 bg-white border border-slate-200 rounded-xl shadow-lg mb-1 max-h-36 overflow-y-auto">
                {filteredMentions.map(target => (
                  <button key={target} onClick={() => insertMention(target)} className="w-full text-left px-3 py-2 text-xs hover:bg-indigo-50 text-slate-600 transition-colors">
                    {target}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input value={chatInput} onChange={e => handleChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                placeholder="Type a message... use @ to mention" className="flex-1 px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
              <button onClick={sendMessage} className="px-3 py-2 bg-indigo-600 text-white rounded-xl text-xs font-semibold hover:bg-indigo-700 transition-colors">
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Rejection Reason Modal ────────────────────────────────────── */}
      {rejectingIssueId && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40" onClick={() => { setRejectingIssueId(null); setRejectReason(''); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-red-600 px-5 py-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2"><XCircle size={16} /> Reject Issue</h3>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-slate-500">Provide a reason so the store manager understands why this issue was rejected.</p>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Reason for rejection..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400/30 focus:border-red-300 resize-none"
                autoFocus
              />
              <div className="flex gap-3">
                <button onClick={() => { setRejectingIssueId(null); setRejectReason(''); }}
                  className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-xs font-semibold hover:bg-slate-200 transition-colors">
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!rejectReason.trim()) return;
                    // Update issue with rejection note then resolve
                    const now = Date.now();
                    const updatedReports = ticket.issueReports.map(r => r.id === rejectingIssueId ? { ...r, status: IssueReportStatus.REJECTED, resolutionNote: rejectReason.trim() } : r);
                    const report = updatedReports.find(r => r.id === rejectingIssueId)!;
                    const autoMsg: TicketMessage = { id: crypto.randomUUID(), ticketId: ticket.id, issueReportId: rejectingIssueId, senderId: user?.id ?? 'system', senderName: user?.name ?? 'System', senderRole: 'admin', content: `Issue "${ISSUE_CATEGORY_LABELS[report.category]}" rejected. Reason: ${rejectReason.trim()}`, createdAt: now };
                    const derivedStatus = deriveTicketStatus(updatedReports, ticket.status);
                    const sla = { ...ticket.sla };
                    if (derivedStatus === TicketStatus.RESOLVED && !sla.resolvedAt) sla.resolvedAt = now;
                    let nextHistory = ticket.statusHistory ?? [];
                    if (derivedStatus !== ticket.status) {
                      nextHistory = [...nextHistory, { status: derivedStatus, timestamp: now, changedBy: user?.id ?? 'system', changedByName: user?.name ?? 'System' }];
                    }
                    onUpdateTicket({ ...ticket, issueReports: updatedReports, status: derivedStatus, statusHistory: nextHistory, sla, messages: [...ticket.messages, autoMsg], updatedAt: now });
                    setRejectingIssueId(null);
                    setRejectReason('');
                  }}
                  disabled={!rejectReason.trim()}
                  className={cn("flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all",
                    rejectReason.trim() ? "bg-red-600 text-white hover:bg-red-700" : "bg-red-200 text-red-400 cursor-not-allowed")}
                >
                  <XCircle size={13} /> Reject Issue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Resolution Settings Modal (Admin) ───────────────────────── */}
      {showResolutionModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40" onClick={() => setShowResolutionModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-emerald-600 px-5 py-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2"><ShieldCheck size={16} /> Resolution Settings</h3>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Auto-Close Timer</label>
                <select value={autoCloseHours} onChange={e => setAutoCloseHours(Number(e.target.value))}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-300 bg-white">
                  {resConfig.autoCloseDurations.map(d => (
                    <option key={d.hours} value={d.hours}>{d.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Resolution Note</label>
                <textarea value={resolutionNote} onChange={e => setResolutionNote(e.target.value)}
                  placeholder="Final message to the manager (what was fixed, how to verify)..."
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-300 resize-none"
                  autoFocus />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                <TriangleAlert size={14} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-[11px] text-amber-700 leading-relaxed">
                  <strong>Note:</strong> If the manager does not respond within the selected timeframe ({autoCloseHours}h), the ticket will automatically transition to <strong>CLOSED</strong>.
                </p>
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setShowResolutionModal(false)}
                  className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-xs font-semibold hover:bg-slate-200 transition-colors">
                  Cancel
                </button>
                <button onClick={confirmResolution}
                  className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 transition-all shadow-sm">
                  <CheckCircle2 size={13} /> Confirm Resolution
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Manager Dispute Modal ─────────────────────────────────────── */}
      {showDisputeModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40" onClick={() => { setShowDisputeModal(false); setDisputeMessage(''); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-red-600 px-5 py-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2"><XCircle size={16} /> Still Have Issues</h3>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-slate-500">Describe what is still not working. The admin will be notified with high priority.</p>
              <textarea value={disputeMessage} onChange={e => setDisputeMessage(e.target.value)}
                placeholder="Describe the remaining issue..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400/30 focus:border-red-300 resize-none"
                autoFocus />
              <div className="flex gap-3">
                <button onClick={() => { setShowDisputeModal(false); setDisputeMessage(''); }}
                  className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-xs font-semibold hover:bg-slate-200 transition-colors">
                  Cancel
                </button>
                <button onClick={managerDispute} disabled={!disputeMessage.trim()}
                  className={cn("flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all",
                    disputeMessage.trim() ? "bg-red-600 text-white hover:bg-red-700" : "bg-red-200 text-red-400 cursor-not-allowed")}>
                  <XCircle size={13} /> Reopen Ticket
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Track Lifecycle Tab (Timeline View) -------------------------------------

function TrackLifecycleTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'support-desk', 'track-lifecycle', 'admin');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    (async () => {
      let all = await loadTickets();
      if (!all.length && user) { all = seedDemoTickets(user.id, user.name); await saveTickets(all); }
      setTickets(all);
    })();
  }, [user]);

  const myTickets = isAdmin ? tickets : tickets.filter(t => t.managerId === user?.id);
  const filtered = myTickets.filter(t =>
    t.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.subject.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((a, b) => b.updatedAt - a.updatedAt);

  // Group tickets by status ladder stage
  const boardColumns = useMemo(() => {
    return STATUS_LADDER.map(status => ({
      status,
      label: TICKET_STATUS_LABELS[status],
      color: STATUS_COLORS[status],
      tickets: filtered.filter(t => t.status === status),
    }));
  }, [filtered]);

  const stageInfo = selectedTicket ? calculateTimeInStage(selectedTicket) : null;
  const history = selectedTicket?.statusHistory ?? [];

  const getStepState = (status: TicketStatus) => {
    if (!selectedTicket) return 'future';
    const idx = STATUS_LADDER.indexOf(status);
    const currentIdx = STATUS_LADDER.indexOf(selectedTicket.status);
    if (idx < currentIdx) return 'completed';
    if (idx === currentIdx) return 'current';
    return 'future';
  };

  const getStepTimestamp = (status: TicketStatus): number | null => {
    if (!selectedTicket) return null;
    return getStatusTimestamp(selectedTicket, status);
  };

  // Column header icon map
  const statusIcons: Record<TicketStatus, React.ElementType> = {
    [TicketStatus.NEW]: Inbox,
    [TicketStatus.OPEN]: Eye,
    [TicketStatus.IN_PROGRESS]: Activity,
    [TicketStatus.PENDING]: Clock,
    [TicketStatus.RESOLVED]: CheckCircle2,
    [TicketStatus.CLOSED]: Shield,
  };

  return (
    <div className="px-4 pb-2 h-[calc(100vh-140px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-bold tracking-tight text-slate-900">Track Lifecycle</h2>
          <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full font-semibold">{filtered.length} ticket{filtered.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search..."
            className="w-44 pl-7 pr-2 py-1.5 text-[11px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-2 flex-1 min-h-0 overflow-hidden">
        {boardColumns.map(col => {
          const Icon = statusIcons[col.status];
          return (
            <div key={col.status} className="flex-1 min-w-0 flex flex-col">
              {/* Column Header */}
              <div className={cn("flex items-center gap-1.5 px-2 py-1.5 rounded-t-xl border border-b-0",
                col.color.bg, "border-slate-200")}>
                <Icon size={12} className={col.color.text} />
                <span className={cn("text-[10px] font-bold truncate", col.color.text)}>{col.label}</span>
                <span className={cn("ml-auto px-1.5 py-px rounded-full text-[9px] font-bold min-w-[16px] text-center",
                  col.color.bg, col.color.text)}>
                  {col.tickets.length}
                </span>
              </div>

              {/* Column Body */}
              <div className="flex-1 bg-slate-50/80 border border-slate-200 rounded-b-xl p-1.5 space-y-1.5 overflow-y-auto">
                {col.tickets.map(ticket => {
                  const sum = issueResolutionSummary(ticket.issueReports);
                  const isSelected = selectedTicket?.id === ticket.id;
                  return (
                    <button key={ticket.id} onClick={() => setSelectedTicket(ticket)}
                      className={cn("w-full text-left px-2 py-1.5 rounded-lg border transition-all bg-white hover:shadow-sm",
                        isSelected
                          ? "border-indigo-500 shadow-sm ring-1 ring-indigo-100"
                          : "border-slate-100 hover:border-slate-300")}>
                      <div className="flex items-center gap-1">
                        <span className="text-[9px] font-mono font-bold text-indigo-600 truncate">{ticket.id}</span>
                        <span className="text-[8px] text-emerald-600 font-semibold ml-auto shrink-0">{sum.resolved}/{sum.total}</span>
                      </div>
                      <div className="text-[10px] font-semibold text-slate-800 leading-tight truncate mt-0.5">{ticket.subject}</div>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-[9px] text-slate-500 truncate">{ticket.managerName}</span>
                        <span className="text-[9px] text-slate-400 ml-auto shrink-0">{timeAgo(ticket.updatedAt)}</span>
                      </div>
                    </button>
                  );
                })}
                {col.tickets.length === 0 && (
                  <div className="flex items-center justify-center py-4">
                    <span className="text-[9px] text-slate-400">Empty</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Ticket Detail Slide-over */}
      {selectedTicket && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setSelectedTicket(null)}>
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" />
          <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right"
            onClick={e => e.stopPropagation()}>
            {/* Detail Header */}
            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono text-slate-400">{selectedTicket.id}</span>
                  <span className={cn("px-2.5 py-0.5 rounded-full text-[10px] font-semibold",
                    STATUS_COLORS[selectedTicket.status].bg, STATUS_COLORS[selectedTicket.status].text)}>
                    {TICKET_STATUS_LABELS[selectedTicket.status]}
                  </span>
                </div>
                <button onClick={() => setSelectedTicket(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                  <X size={18} />
                </button>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mt-2">{selectedTicket.subject}</h3>
              <div className="flex items-center gap-3 mt-1">
                <p className="text-xs text-slate-500">by {selectedTicket.managerName}</p>
                <span className="text-[10px] text-slate-400">{formatDate(selectedTicket.createdAt)}</span>
                {stageInfo && (
                  <div className="flex items-center gap-1.5 ml-auto">
                    <Clock size={12} className="text-indigo-500" />
                    <span className="text-xs font-bold text-indigo-600">{formatDuration(stageInfo.durationMs)}</span>
                    <span className="text-[10px] text-slate-400">in {TICKET_STATUS_LABELS[stageInfo.currentStatus]}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-5 space-y-6">
              {/* Issue Reports Table */}
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText size={14} className="text-indigo-500" /> Issue Reports
                  <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{selectedTicket.issueReports.length}</span>
                </h4>
                <div className="space-y-2">
                  {selectedTicket.issueReports.map(ir => (
                    <div key={ir.id} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className={cn("px-1.5 py-0.5 rounded text-[9px] font-bold", ISSUE_CATEGORY_COLORS[ir.category])}>
                          {ISSUE_CATEGORY_LABELS[ir.category]}
                        </span>
                        <span className="text-[10px] text-slate-400">{ISSUE_MODULE_LABELS[ir.module]}</span>
                        <span className={cn("ml-auto px-1.5 py-0.5 rounded text-[9px] font-bold",
                          ISSUE_REPORT_STATUS_COLORS[ir.status])}>
                          {ISSUE_REPORT_STATUS_LABELS[ir.status]}
                        </span>
                      </div>
                      <p className="text-xs text-slate-700 leading-relaxed">{ir.details}</p>
                      {ir.resolutionNote && (
                        <p className="text-[10px] text-emerald-600 mt-1 italic">Resolution: {ir.resolutionNote}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Status Ladder Stepper */}
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Activity size={14} className="text-indigo-500" /> Status Ladder
                </h4>
                <div className="relative">
                  {STATUS_LADDER.map((status, idx) => {
                    const state = getStepState(status);
                    const ts = getStepTimestamp(status);
                    const historyEntry = history.find(h => h.status === status);
                    const isLast = idx === STATUS_LADDER.length - 1;

                    return (
                      <div key={status} className="flex gap-4 relative">
                        {!isLast && (
                          <div className={cn("absolute left-[15px] top-[30px] w-0.5 h-[calc(100%-10px)]",
                            state === 'completed' ? "bg-emerald-400" : state === 'current' ? "bg-indigo-300" : "bg-slate-200")} />
                        )}
                        <div className="relative z-10 shrink-0">
                          {state === 'completed' ? (
                            <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center shadow-sm">
                              <Check size={14} className="text-white" />
                            </div>
                          ) : state === 'current' ? (
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center shadow-sm ring-4 ring-indigo-100/50">
                              <div className="w-3 h-3 rounded-full bg-indigo-600 animate-pulse" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                              <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                            </div>
                          )}
                        </div>
                        <div className={cn("flex-1 pb-6", isLast && "pb-0")}>
                          <div className="flex items-center gap-3">
                            <span className={cn("text-sm font-bold",
                              state === 'completed' ? "text-emerald-700" :
                              state === 'current' ? "text-indigo-700" : "text-slate-400")}>
                              {TICKET_STATUS_LABELS[status]}
                            </span>
                            <span className={cn("px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider",
                              state === 'completed' ? "bg-emerald-100 text-emerald-700" :
                              state === 'current' ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-400")}>
                              {state === 'completed' ? 'Done' : state === 'current' ? 'Current' : 'Upcoming'}
                            </span>
                          </div>
                          {ts && <p className="text-[11px] text-slate-500 mt-1">{formatDate(ts)}</p>}
                          {historyEntry && historyEntry.changedByName !== 'System' && (
                            <p className="text-[10px] text-slate-400 mt-0.5">by {historyEntry.changedByName}</p>
                          )}
                          {state === 'current' && stageInfo && (
                            <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-xl">
                              <Clock size={12} className="text-indigo-500" />
                              <span className="text-xs font-semibold text-indigo-700">{formatDuration(stageInfo.durationMs)}</span>
                              <span className="text-[10px] text-indigo-400">in this stage</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Transition History */}
              {history.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <RotateCcw size={14} className="text-indigo-500" /> Transition History
                  </h4>
                  <div className="space-y-1.5">
                    {history.map((entry, idx) => {
                      const prev = idx > 0 ? history[idx - 1] : null;
                      const timeSincePrev = prev ? entry.timestamp - prev.timestamp : 0;
                      return (
                        <div key={idx} className="flex items-center gap-3 py-2 px-3 bg-slate-50 rounded-xl">
                          <div className={cn("w-2 h-2 rounded-full shrink-0", STATUS_COLORS[entry.status].dot)} />
                          <span className={cn("text-xs font-semibold", STATUS_COLORS[entry.status].text)}>{TICKET_STATUS_LABELS[entry.status]}</span>
                          <span className="text-[10px] text-slate-400">{formatDate(entry.timestamp)}</span>
                          {timeSincePrev > 0 && (
                            <span className="text-[10px] text-slate-400 ml-auto">+{formatDuration(timeSincePrev)}</span>
                          )}
                          <span className="text-[10px] text-slate-400">by {entry.changedByName}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Operational Insights Tab (Advanced Intelligence) -------------------------

type InsightsSection = 'chronic' | 'performance' | 'trends' | 'export';

interface ChronicIssue {
  key: string;
  brandId: string;
  brandName: string;
  aggregator: string;
  category: IssueCategory;
  count: number;
  ticketIds: string[];
  lostHoursMs: number;
}

interface AggregatorRanking {
  name: string;
  totalIssues: number;
  avgResolutionMs: number;
  pct: number;
}

interface SeverityRow {
  category: IssueCategory;
  label: string;
  count: number;
  avgResolutionMs: number;
}

interface ManagerScore {
  id: string;
  name: string;
  totalTickets: number;
  rejectedIssues: number;
  incompleteRate: number;
}

interface TrendPoint {
  label: string;
  received: number;
  resolved: number;
  ts: number;
}

interface CategoryTrend {
  label: string;
  counts: Record<string, number>;
}

interface PeakDay {
  day: string;
  count: number;
  pct: number;
}

function OperationalInsightsTab({ user }: { user: User | null }) {
  const isAdmin = hasTabVariant(user, 'support-desk', 'operational-insights', 'admin');
  const isSuperAdmin = isAdmin;
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [activeSection, setActiveSection] = useState<InsightsSection>('chronic');
  const [chronicDrillTickets, setChronicDrillTickets] = useState<string[] | null>(null);
  const navigate = useNavigate();
  const now = Date.now();
  const DAY = 86400000;
  const THIRTY_DAYS = 30 * DAY;

  useEffect(() => {
    (async () => {
      let all = await loadTickets();
      if (!all.length && user) { all = seedDemoTickets(user.id, user.name); await saveTickets(all); }
      setTickets(all);
      const repo = getRepository();
      const [b, u] = await Promise.all([repo.getBrands(), repo.getUsers()]);
      setBrands(b.filter(br => br.enabledModules?.includes('support-desk')));
      setAllUsers(u);
    })();
  }, [user]);

  // --- Scope: Admin sees all, Manager sees only their own ---
  const myTickets = isAdmin ? tickets : tickets.filter(t => t.managerId === user?.id);
  const allIssues = myTickets.flatMap(t => t.issueReports);
  const brandMap = useMemo(() => new Map(brands.map(b => [b.id, b.name])), [brands]);

  // ======= CHRONIC ISSUE DETECTOR =======
  const chronicIssues: ChronicIssue[] = useMemo(() => {
    const window = myTickets.filter(t => now - t.createdAt < THIRTY_DAYS);
    const map = new Map<string, ChronicIssue>();
    window.forEach(ticket => {
      ticket.issueReports.forEach(ir => {
        ir.brandIds.forEach(bid => {
          ir.aggregators.forEach(agg => {
            const key = `${bid}|${agg}|${ir.category}`;
            const existing = map.get(key);
            const resMs = ticket.sla.resolvedAt ? ticket.sla.resolvedAt - ticket.sla.createdAt : now - ticket.sla.createdAt;
            if (existing) {
              existing.count++;
              existing.lostHoursMs += resMs;
              if (!existing.ticketIds.includes(ticket.id)) existing.ticketIds.push(ticket.id);
            } else {
              map.set(key, {
                key, brandId: bid,
                brandName: brandMap.get(bid) ?? bid,
                aggregator: agg, category: ir.category,
                count: 1, ticketIds: [ticket.id], lostHoursMs: resMs,
              });
            }
          });
        });
      });
    });
    return [...map.values()].filter(c => c.count > 3).sort((a, b) => b.count - a.count);
  }, [myTickets, brandMap, now]);

  // ======= PERFORMANCE: Aggregator Ranking =======
  const aggregatorRanking: AggregatorRanking[] = useMemo(() => {
    const map: Record<string, { issues: number; resMs: number[]; }> = {};
    myTickets.forEach(t => t.issueReports.forEach(ir => {
      ir.aggregators.forEach(agg => {
        if (!map[agg]) map[agg] = { issues: 0, resMs: [] };
        map[agg].issues++;
        if (t.sla.resolvedAt) map[agg].resMs.push(t.sla.resolvedAt - t.sla.createdAt);
      });
    }));
    const entries = Object.entries(map).map(([name, d]) => ({
      name, totalIssues: d.issues,
      avgResolutionMs: d.resMs.length > 0 ? d.resMs.reduce((a, b) => a + b, 0) / d.resMs.length : 0,
      pct: 0,
    })).sort((a, b) => b.totalIssues - a.totalIssues);
    const max = entries.length > 0 ? entries[0].totalIssues : 1;
    return entries.map(e => ({ ...e, pct: Math.round((e.totalIssues / max) * 100) }));
  }, [myTickets]);

  // ======= PERFORMANCE: Severity Matrix =======
  const severityMatrix: SeverityRow[] = useMemo(() => {
    const map: Record<string, { count: number; resMs: number[] }> = {};
    myTickets.forEach(t => t.issueReports.forEach(ir => {
      if (!map[ir.category]) map[ir.category] = { count: 0, resMs: [] };
      map[ir.category].count++;
      if (t.sla.resolvedAt) map[ir.category].resMs.push(t.sla.resolvedAt - t.sla.createdAt);
    }));
    return Object.entries(map).map(([cat, d]) => ({
      category: cat as IssueCategory,
      label: ISSUE_CATEGORY_LABELS[cat as IssueCategory] ?? cat,
      count: d.count,
      avgResolutionMs: d.resMs.length > 0 ? d.resMs.reduce((a, b) => a + b, 0) / d.resMs.length : 0,
    })).sort((a, b) => b.avgResolutionMs - a.avgResolutionMs);
  }, [myTickets]);

  // ======= PERFORMANCE: Manager Reliability =======
  const managerScores: ManagerScore[] = useMemo(() => {
    if (!isAdmin) return [];
    const map: Record<string, { name: string; total: number; rejected: number }> = {};
    myTickets.forEach(t => {
      if (!map[t.managerId]) map[t.managerId] = { name: t.managerName, total: 0, rejected: 0 };
      map[t.managerId].total++;
      map[t.managerId].rejected += t.issueReports.filter(ir => ir.status === IssueReportStatus.REJECTED).length;
    });
    return Object.entries(map).map(([id, d]) => ({
      id, name: d.name, totalTickets: d.total, rejectedIssues: d.rejected,
      incompleteRate: d.total > 0 ? Math.round((d.rejected / (d.total * 1)) * 100) : 0,
    })).sort((a, b) => b.incompleteRate - a.incompleteRate);
  }, [myTickets, isAdmin]);

  // ======= TRENDS: Volume (last 6 months) =======
  const volumeTrend: TrendPoint[] = useMemo(() => {
    const months: TrendPoint[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now);
      d.setMonth(d.getMonth() - i);
      const yr = d.getFullYear();
      const mo = d.getMonth();
      const start = new Date(yr, mo, 1).getTime();
      const end = new Date(yr, mo + 1, 1).getTime();
      const received = myTickets.filter(t => t.createdAt >= start && t.createdAt < end).length;
      const resolved = myTickets.filter(t => t.sla.resolvedAt && t.sla.resolvedAt >= start && t.sla.resolvedAt < end).length;
      months.push({ label: d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }), received, resolved, ts: start });
    }
    return months;
  }, [myTickets, now]);
  const volumeMax = Math.max(...volumeTrend.map(p => Math.max(p.received, p.resolved)), 1);

  // ======= TRENDS: Category Shift (top 4 categories, last 6 months) =======
  const categoryTrend: { months: string[]; series: { category: string; label: string; data: number[] }[] } = useMemo(() => {
    const catCounts: Record<string, Record<string, number>> = {};
    const months: string[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now);
      d.setMonth(d.getMonth() - i);
      const key = d.toLocaleDateString(undefined, { month: 'short' });
      months.push(key);
      const yr = d.getFullYear();
      const mo = d.getMonth();
      const start = new Date(yr, mo, 1).getTime();
      const end = new Date(yr, mo + 1, 1).getTime();
      const monthTickets = myTickets.filter(t => t.createdAt >= start && t.createdAt < end);
      monthTickets.forEach(t => t.issueReports.forEach(ir => {
        if (!catCounts[ir.category]) catCounts[ir.category] = {};
        catCounts[ir.category][key] = (catCounts[ir.category][key] || 0) + 1;
      }));
    }
    // Top 4 by total
    const totals = Object.entries(catCounts).map(([cat, mc]) => ({
      cat, total: Object.values(mc).reduce((a, b) => a + b, 0),
    })).sort((a, b) => b.total - a.total).slice(0, 4);
    const series = totals.map(({ cat }) => ({
      category: cat,
      label: ISSUE_CATEGORY_LABELS[cat as IssueCategory] ?? cat,
      data: months.map(m => catCounts[cat][m] || 0),
    }));
    return { months, series };
  }, [myTickets, now]);

  // ======= TRENDS: Peak Days =======
  const peakDays: PeakDay[] = useMemo(() => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const counts = new Array(7).fill(0);
    myTickets.forEach(t => { counts[new Date(t.createdAt).getDay()]++; });
    const max = Math.max(...counts, 1);
    return dayNames.map((day, i) => ({ day, count: counts[i], pct: Math.round((counts[i] / max) * 100) }));
  }, [myTickets]);

  // ======= EXPORT: Pre-compute sheet data for UI + download =======
  const sheet1Rows = useMemo(() => myTickets.flatMap(t => t.issueReports.map(ir => ({
    ticketId: t.id, subject: t.subject, manager: t.managerName,
    status: TICKET_STATUS_LABELS[t.status],
    category: ISSUE_CATEGORY_LABELS[ir.category],
    brands: ir.brandIds.map(bid => brandMap.get(bid) ?? bid).join(', '),
    aggregators: ir.aggregators.join(', '),
    issueStatus: ir.status,
    issueStatusLabel: ISSUE_REPORT_STATUS_LABELS[ir.status],
    resolutionNote: ir.resolutionNote ?? '',
    created: new Date(t.createdAt).toISOString(),
    firstResponse: t.sla.firstResponseAt ? new Date(t.sla.firstResponseAt).toISOString() : '',
    resolvedAt: t.sla.resolvedAt ? new Date(t.sla.resolvedAt).toISOString() : '',
    durationHrs: t.sla.resolvedAt ? ((t.sla.resolvedAt - t.sla.createdAt) / 3600000).toFixed(1) : 'Open',
    durationMs: t.sla.resolvedAt ? t.sla.resolvedAt - t.sla.createdAt : 0,
  }))), [myTickets, brandMap]);

  const sheet2Rows = useMemo(() => {
    const map: Record<string, { brand: string; aggregator: string; issues: number; resolved: number; avgHrs: number[] }> = {};
    myTickets.forEach(t => t.issueReports.forEach(ir => {
      ir.brandIds.forEach(bid => ir.aggregators.forEach(agg => {
        const k = `${bid}|${agg}`;
        if (!map[k]) map[k] = { brand: brandMap.get(bid) ?? bid, aggregator: agg, issues: 0, resolved: 0, avgHrs: [] };
        map[k].issues++;
        if (ir.status === IssueReportStatus.RESOLVED && t.sla.resolvedAt) {
          map[k].resolved++;
          map[k].avgHrs.push((t.sla.resolvedAt - t.sla.createdAt) / 3600000);
        }
      }));
    }));
    return Object.values(map).map(s => ({
      brand: s.brand, aggregator: s.aggregator, totalIssues: s.issues, resolved: s.resolved,
      avgHrs: s.avgHrs.length > 0 ? (s.avgHrs.reduce((a, b) => a + b, 0) / s.avgHrs.length).toFixed(1) : 'N/A',
      resRate: s.issues > 0 ? Math.round((s.resolved / s.issues) * 100) : 0,
    }));
  }, [myTickets, brandMap]);

  const sheet3Rows = useMemo(() => chronicIssues.map(c => ({
    brand: c.brandName, aggregator: c.aggregator,
    issueType: ISSUE_CATEGORY_LABELS[c.category],
    occurrences: c.count, tickets: c.ticketIds.join(', '),
    lostHrs: (c.lostHoursMs / 3600000).toFixed(1),
  })), [chronicIssues]);

  const generateIntelligenceReport = useCallback(() => {
    const toTSV = (rows: Record<string, string | number>[]): string => {
      if (rows.length === 0) return 'No data';
      const headers = Object.keys(rows[0]);
      return [headers.join('\t'), ...rows.map(r => headers.map(h => String(r[h] ?? '')).join('\t'))].join('\n');
    };
    const s1 = sheet1Rows.map(r => ({ 'Ticket ID': r.ticketId, 'Subject': r.subject, 'Manager': r.manager, 'Status': r.status, 'Issue Category': r.category, 'Brands': r.brands, 'Aggregators': r.aggregators, 'Issue Status': r.issueStatusLabel, 'Resolution Note': r.resolutionNote, 'Created': r.created, 'First Response': r.firstResponse, 'Resolved At': r.resolvedAt, 'Resolution Duration (hrs)': r.durationHrs }));
    const s2 = sheet2Rows.map(r => ({ 'Brand': r.brand, 'Aggregator': r.aggregator, 'Total Issues': r.totalIssues, 'Resolved': r.resolved, 'Avg Resolution (hrs)': r.avgHrs }));
    const s3 = sheet3Rows.map(r => ({ 'Brand': r.brand, 'Aggregator': r.aggregator, 'Issue Type': r.issueType, 'Occurrences (30d)': r.occurrences, 'Related Tickets': r.tickets, 'Lost Hours': r.lostHrs }));
    const content = [
      '=== SHEET 1: INDIVIDUAL ISSUE REPORTS ===\n', toTSV(s1),
      '\n\n=== SHEET 2: BRAND × AGGREGATOR SUMMARY ===\n', toTSV(s2),
      '\n\n=== SHEET 3: CHRONIC ISSUES & LOST HOURS ===\n', toTSV(s3),
    ].join('');
    const blob = new Blob([content], { type: 'text/tab-separated-values;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Intelligence_Report_${new Date().toISOString().slice(0, 10)}.tsv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }, [sheet1Rows, sheet2Rows, sheet3Rows]);

  // --- Deep Navy theme helpers ---
  const navyBg = 'bg-[#0f172a]';
  const navyCard = 'bg-[#1e293b]';
  const navyBorder = 'border-[#334155]';
  const silverText = 'text-slate-300';
  const silverMuted = 'text-slate-400';
  const silverBright = 'text-slate-100';

  const sectionNav: { key: InsightsSection; label: string; icon: React.ElementType }[] = [
    { key: 'chronic', label: 'Chronic Issues', icon: Repeat },
    { key: 'performance', label: 'Performance', icon: Target },
    { key: 'trends', label: 'Trend Analysis', icon: TrendingUp },
    { key: 'export', label: 'BI Export', icon: Download },
  ];

  const goTicket = (id: string) => navigate(`/apps/support-desk/ticket-center?open=${id}`);

  // --- View scoping label ---
  const scopeLabel = isSuperAdmin ? 'God Mode — All Organizations'
    : isAdmin ? 'Organization-Wide Intelligence'
    : 'My Brand Health';

  return (
    <div className={cn("min-h-screen rounded-2xl overflow-hidden", navyBg)}>
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-[#334155]">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-indigo-600/20 rounded-xl flex items-center justify-center">
                <Shield size={20} className="text-indigo-400" />
              </div>
              <div>
                <h2 className={cn("text-xl font-bold tracking-tight", silverBright)}>Operational Intelligence</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={cn("text-[10px] font-semibold uppercase tracking-widest", silverMuted)}>{scopeLabel}</span>
                  <span className="text-[10px] text-slate-500">•</span>
                  <span className="text-[10px] text-slate-500">{myTickets.length} tickets analyzed</span>
                </div>
              </div>
            </div>
          </div>
          <button onClick={generateIntelligenceReport}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold transition-colors shadow-lg shadow-indigo-900/30">
            <Download size={14} />
            Generate Intelligence Report
          </button>
        </div>

        {/* Section Nav */}
        <div className="flex gap-1 mt-5">
          {sectionNav.map(s => (
            <button key={s.key} onClick={() => { setActiveSection(s.key); setChronicDrillTickets(null); }}
              className={cn("flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all",
                activeSection === s.key
                  ? "bg-indigo-600/30 text-indigo-300 shadow-inner"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50")}>
              <s.icon size={14} />
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-5 space-y-5">

        {/* ============ CHRONIC ISSUE DETECTOR ============ */}
        {activeSection === 'chronic' && (
          <div className="space-y-4">
            {/* Chronic Drill-down View */}
            {chronicDrillTickets !== null ? (
              <div className="space-y-3">
                <button onClick={() => setChronicDrillTickets(null)}
                  className="flex items-center gap-2 text-xs text-indigo-400 hover:text-indigo-300 font-semibold transition-colors">
                  <ArrowLeft size={14} /> Back to Chronic Issues
                </button>
                <div className={cn("rounded-xl border p-5", navyCard, navyBorder)}>
                  <h3 className={cn("text-sm font-bold mb-3", silverBright)}>Related Tickets</h3>
                  <div className="space-y-2">
                    {chronicDrillTickets.map(tid => {
                      const t = myTickets.find(tk => tk.id === tid);
                      if (!t) return null;
                      return (
                        <button key={tid} onClick={() => goTicket(tid)}
                          className={cn("w-full flex items-center gap-4 px-4 py-3 rounded-lg text-left transition-colors", "hover:bg-slate-700/50", navyCard)}>
                          <div className={cn("w-2 h-2 rounded-full shrink-0", STATUS_COLORS[t.status].dot)} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-slate-400">{t.id}</span>
                              <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-semibold", STATUS_COLORS[t.status].bg, STATUS_COLORS[t.status].text)}>{TICKET_STATUS_LABELS[t.status]}</span>
                            </div>
                            <div className={cn("text-sm font-semibold truncate mt-0.5", silverBright)}>{t.subject}</div>
                          </div>
                          <div className="text-[10px] text-slate-500 shrink-0">{timeAgo(t.updatedAt)}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className={cn("rounded-xl border p-4", navyCard, navyBorder)}>
                    <div className="flex items-center gap-2 mb-2">
                      <Flame size={16} className="text-red-400" />
                      <span className={cn("text-[10px] font-semibold uppercase tracking-wider", silverMuted)}>Chronic Patterns</span>
                    </div>
                    <div className={cn("text-3xl font-black", silverBright)}>{chronicIssues.length}</div>
                    <div className="text-[10px] text-slate-500 mt-1">Detected in last 30 days</div>
                  </div>
                  <div className={cn("rounded-xl border p-4", navyCard, navyBorder)}>
                    <div className="flex items-center gap-2 mb-2">
                      <Clock size={16} className="text-amber-400" />
                      <span className={cn("text-[10px] font-semibold uppercase tracking-wider", silverMuted)}>Total Lost Hours</span>
                    </div>
                    <div className={cn("text-3xl font-black", silverBright)}>
                      {(chronicIssues.reduce((s, c) => s + c.lostHoursMs, 0) / 3600000).toFixed(0)}h
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">Cumulative handling time</div>
                  </div>
                  <div className={cn("rounded-xl border p-4", navyCard, navyBorder)}>
                    <div className="flex items-center gap-2 mb-2">
                      <Zap size={16} className="text-indigo-400" />
                      <span className={cn("text-[10px] font-semibold uppercase tracking-wider", silverMuted)}>Affected Tickets</span>
                    </div>
                    <div className={cn("text-3xl font-black", silverBright)}>
                      {new Set(chronicIssues.flatMap(c => c.ticketIds)).size}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">Unique tickets involved</div>
                  </div>
                </div>

                {/* Chronic Issues List */}
                {chronicIssues.length > 0 ? (
                  <div className={cn("rounded-xl border overflow-hidden", navyCard, navyBorder)}>
                    <div className="px-5 py-3 border-b border-[#334155] flex items-center gap-2">
                      <AlertCircle size={16} className="text-red-400" />
                      <h3 className={cn("text-sm font-bold", silverBright)}>System Alerts — Repeat Offenders</h3>
                    </div>
                    <div className="divide-y divide-[#334155]">
                      {chronicIssues.map(ci => (
                        <div key={ci.key} className="px-5 py-4 flex items-center gap-4 hover:bg-slate-800/30 transition-colors">
                          <div className="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center shrink-0">
                            <Repeat size={18} className="text-red-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={cn("text-sm font-semibold", silverBright)}>
                              <span className="text-red-400">System Alert:</span>{' '}
                              <span className="text-amber-300">{ci.brandName}</span> has had{' '}
                              <span className="text-white font-black">{ci.count}</span>{' '}
                              <span className="text-indigo-300">{ISSUE_CATEGORY_LABELS[ci.category]}</span> issues on{' '}
                              <span className="text-cyan-300">{ci.aggregator}</span> this month
                            </p>
                            <div className="flex items-center gap-3 mt-1.5">
                              <span className="text-[10px] text-slate-500">{ci.ticketIds.length} tickets</span>
                              <span className="text-[10px] text-slate-500">•</span>
                              <span className="text-[10px] text-amber-400/80">{(ci.lostHoursMs / 3600000).toFixed(1)}h lost</span>
                            </div>
                          </div>
                          <button onClick={() => setChronicDrillTickets(ci.ticketIds)}
                            className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600/20 hover:bg-indigo-600/40 text-indigo-300 rounded-lg text-xs font-semibold transition-colors shrink-0">
                            <Eye size={13} /> View Root Cause
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={cn("rounded-xl border p-12 text-center", navyCard, navyBorder)}>
                    <div className="w-16 h-16 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                      <CheckCircle2 size={32} className="text-emerald-400" />
                    </div>
                    <h3 className={cn("text-base font-bold mb-1", silverBright)}>No Chronic Issues Detected</h3>
                    <p className="text-xs text-slate-500 max-w-sm mx-auto">No Brand+Aggregator+Category combination has exceeded 3 occurrences in the last 30 days.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ============ PERFORMANCE DEEP-DIVE ============ */}
        {activeSection === 'performance' && (
          <div className="space-y-5">

            {/* Aggregator Ranking */}
            <div className={cn("rounded-xl border overflow-hidden", navyCard, navyBorder)}>
              <div className="px-5 py-3 border-b border-[#334155]">
                <h3 className={cn("text-sm font-bold", silverBright)}>Total Volume by Aggregator</h3>
                <p className="text-[10px] text-slate-500">Which partner is causing the most support overhead</p>
              </div>
              <div className="px-5 py-4">
                {aggregatorRanking.length > 0 ? (
                  <div className="space-y-3">
                    {aggregatorRanking.map((agg, idx) => (
                      <div key={agg.name} className="flex items-center gap-4">
                        <span className="text-xs font-bold text-slate-500 w-6 text-right">#{idx + 1}</span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className={cn("text-xs font-semibold", silverBright)}>{agg.name}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-[10px] text-slate-500">Avg: {agg.avgResolutionMs > 0 ? formatDuration(agg.avgResolutionMs) : '—'}</span>
                              <span className={cn("text-xs font-bold px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300")}>{agg.totalIssues}</span>
                            </div>
                          </div>
                          <div className="h-2 bg-[#0f172a] rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-indigo-600 to-indigo-400 rounded-full transition-all" style={{ width: `${agg.pct}%` }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic py-4 text-center">No aggregator data yet.</p>
                )}
              </div>
            </div>

            {/* Issue Severity Matrix */}
            <div className={cn("rounded-xl border overflow-hidden", navyCard, navyBorder)}>
              <div className="px-5 py-3 border-b border-[#334155]">
                <h3 className={cn("text-sm font-bold", silverBright)}>Issue Severity Matrix</h3>
                <p className="text-[10px] text-slate-500">Issue Type vs. Average Resolution Time</p>
              </div>
              {severityMatrix.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#334155]">
                      <th className={cn("px-5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider", silverMuted)}>Issue Type</th>
                      <th className={cn("px-5 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider", silverMuted)}>Volume</th>
                      <th className={cn("px-5 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider", silverMuted)}>Avg Resolution</th>
                      <th className={cn("px-5 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wider", silverMuted)}>Impact</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#334155]">
                    {severityMatrix.map(row => {
                      const impactLevel = row.avgResolutionMs > 24 * 3600000 ? 'Critical' : row.avgResolutionMs > 8 * 3600000 ? 'High' : row.avgResolutionMs > 2 * 3600000 ? 'Medium' : 'Low';
                      const impactColor = impactLevel === 'Critical' ? 'text-red-400 bg-red-500/10' : impactLevel === 'High' ? 'text-amber-400 bg-amber-500/10' : impactLevel === 'Medium' ? 'text-sky-400 bg-sky-500/10' : 'text-emerald-400 bg-emerald-500/10';
                      return (
                        <tr key={row.category} className="hover:bg-slate-800/30 transition-colors">
                          <td className={cn("px-5 py-3 text-xs font-semibold", silverBright)}>{row.label}</td>
                          <td className="px-5 py-3 text-center">
                            <span className="text-xs font-bold text-indigo-300">{row.count}</span>
                          </td>
                          <td className={cn("px-5 py-3 text-center text-xs font-semibold", silverText)}>
                            {row.avgResolutionMs > 0 ? formatDuration(row.avgResolutionMs) : '—'}
                          </td>
                          <td className="px-5 py-3 text-right">
                            <span className={cn("text-[10px] font-bold px-2 py-1 rounded-full", impactColor)}>{impactLevel}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="px-5 py-8 text-center">
                  <p className="text-xs text-slate-500 italic">No issue data available.</p>
                </div>
              )}
            </div>

            {/* Manager Reliability (Admin only) */}
            {isAdmin && (
              <div className={cn("rounded-xl border overflow-hidden", navyCard, navyBorder)}>
                <div className="px-5 py-3 border-b border-[#334155]">
                  <h3 className={cn("text-sm font-bold", silverBright)}>Manager Reliability Score</h3>
                  <p className="text-[10px] text-slate-500">Managers with high rejection rates may need training</p>
                </div>
                {managerScores.length > 0 ? (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#334155]">
                        <th className={cn("px-5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider", silverMuted)}>Manager</th>
                        <th className={cn("px-5 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider", silverMuted)}>Total Tickets</th>
                        <th className={cn("px-5 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider", silverMuted)}>Rejected Issues</th>
                        <th className={cn("px-5 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wider", silverMuted)}>Reliability</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#334155]">
                      {managerScores.map(m => {
                        const reliability = 100 - m.incompleteRate;
                        const reliColor = reliability >= 90 ? 'text-emerald-400' : reliability >= 70 ? 'text-amber-400' : 'text-red-400';
                        return (
                          <tr key={m.id} className="hover:bg-slate-800/30 transition-colors">
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 bg-slate-700 rounded-full flex items-center justify-center">
                                  <UserIcon size={13} className="text-slate-300" />
                                </div>
                                <span className={cn("text-xs font-semibold", silverBright)}>{m.name}</span>
                              </div>
                            </td>
                            <td className="px-5 py-3 text-center"><span className="text-xs font-bold text-slate-300">{m.totalTickets}</span></td>
                            <td className="px-5 py-3 text-center"><span className={cn("text-xs font-bold", m.rejectedIssues > 0 ? 'text-red-400' : 'text-slate-500')}>{m.rejectedIssues}</span></td>
                            <td className="px-5 py-3 text-right">
                              <div className="inline-flex items-center gap-2">
                                <div className="w-16 h-1.5 bg-[#0f172a] rounded-full overflow-hidden">
                                  <div className={cn("h-full rounded-full", reliability >= 90 ? 'bg-emerald-500' : reliability >= 70 ? 'bg-amber-500' : 'bg-red-500')} style={{ width: `${reliability}%` }} />
                                </div>
                                <span className={cn("text-xs font-bold", reliColor)}>{reliability}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="px-5 py-8 text-center">
                    <p className="text-xs text-slate-500 italic">{isAdmin ? 'No manager data available.' : 'Visible to admins only.'}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ============ TREND ANALYSIS ============ */}
        {activeSection === 'trends' && (
          <div className="space-y-5">

            {/* Volume Trend — Received vs Resolved (bar chart) */}
            <div className={cn("rounded-xl border p-5", navyCard, navyBorder)}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={cn("text-sm font-bold", silverBright)}>Volume Trend — 6 Months</h3>
                  <p className="text-[10px] text-slate-500">Tickets received vs resolved each month</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-indigo-500" /><span className="text-[10px] text-slate-400">Received</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /><span className="text-[10px] text-slate-400">Resolved</span></div>
                </div>
              </div>
              <div className="flex items-end gap-3 h-40">
                {volumeTrend.map((point, pi) => (
                  <div key={`${point.label}-${pi}`} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex items-end justify-center gap-1 h-32">
                      <div className="relative group w-5">
                        <div className="bg-indigo-500 rounded-t-sm w-full transition-all" style={{ height: `${(point.received / volumeMax) * 100}%`, minHeight: point.received > 0 ? '4px' : '0' }} />
                        <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-900 text-white text-[9px] px-2 py-1 rounded shadow z-10 whitespace-nowrap">{point.received} received</div>
                      </div>
                      <div className="relative group w-5">
                        <div className="bg-emerald-500 rounded-t-sm w-full transition-all" style={{ height: `${(point.resolved / volumeMax) * 100}%`, minHeight: point.resolved > 0 ? '4px' : '0' }} />
                        <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-900 text-white text-[9px] px-2 py-1 rounded shadow z-10 whitespace-nowrap">{point.resolved} resolved</div>
                      </div>
                    </div>
                    <span className="text-[9px] text-slate-500 font-medium">{point.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Shift — Sparkline per category */}
            <div className={cn("rounded-xl border p-5", navyCard, navyBorder)}>
              <h3 className={cn("text-sm font-bold mb-1", silverBright)}>Category Shift Analysis</h3>
              <p className="text-[10px] text-slate-500 mb-4">How issue type volumes change over time (top 4 categories)</p>
              {categoryTrend.series.length > 0 ? (
                <div className="space-y-4">
                  {(() => {
                    const TREND_COLORS = ['text-indigo-400', 'text-amber-400', 'text-cyan-400', 'text-rose-400'];
                    const TREND_DOT_COLORS = ['bg-indigo-400', 'bg-amber-400', 'bg-cyan-400', 'bg-rose-400'];
                    const TREND_BAR_COLORS = ['bg-indigo-500/30', 'bg-amber-500/30', 'bg-cyan-500/30', 'bg-rose-500/30'];
                    return categoryTrend.series.map((s, si) => {
                      const maxVal = Math.max(...s.data, 1);
                      return (
                        <div key={s.category}>
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className={cn("w-2 h-2 rounded-full", TREND_DOT_COLORS[si])} />
                            <span className={cn("text-xs font-semibold", TREND_COLORS[si])}>{s.label}</span>
                          </div>
                          <div className="flex items-end gap-1.5 h-10">
                            {s.data.map((val, mi) => (
                              <div key={mi} className="flex-1 relative group">
                                <div className={cn("w-full rounded-t-sm transition-all", TREND_BAR_COLORS[si])} style={{ height: `${(val / maxVal) * 100}%`, minHeight: val > 0 ? '3px' : '0' }} />
                                <div className="absolute -top-6 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-900 text-white text-[9px] px-1.5 py-0.5 rounded shadow z-10">{val}</div>
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-1.5 mt-0.5">
                            {categoryTrend.months.map((m, mi) => (
                              <span key={`${m}-${mi}`} className="flex-1 text-center text-[8px] text-slate-600">{m}</span>
                            ))}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic py-4 text-center">Not enough data for trend analysis.</p>
              )}
            </div>

            {/* Peak Days */}
            <div className={cn("rounded-xl border p-5", navyCard, navyBorder)}>
              <h3 className={cn("text-sm font-bold mb-1", silverBright)}>Peak Time Analysis</h3>
              <p className="text-[10px] text-slate-500 mb-4">Which days of the week see the highest ticket volume</p>
              <div className="flex items-end gap-2 h-28">
                {peakDays.map(pd => (
                  <div key={pd.day} className="flex-1 flex flex-col items-center gap-1.5">
                    <div className="relative group w-full">
                      <div className={cn("w-full rounded-t-md transition-all mx-auto", pd.pct === 100 ? 'bg-gradient-to-t from-red-600 to-red-400' : pd.pct >= 70 ? 'bg-gradient-to-t from-amber-600 to-amber-400' : 'bg-gradient-to-t from-indigo-600 to-indigo-400')}
                        style={{ height: `${(pd.pct / 100) * 80}px`, minHeight: pd.count > 0 ? '6px' : '0' }} />
                      <div className="absolute -top-6 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-900 text-white text-[9px] px-2 py-1 rounded shadow z-10">{pd.count} tickets</div>
                    </div>
                    <span className={cn("text-[10px] font-semibold", pd.pct === 100 ? 'text-red-400' : 'text-slate-400')}>{pd.day}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ============ BI EXPORT ============ */}
        {activeSection === 'export' && (
          <div className="space-y-5">
            {/* Header — Summary + Download */}
            <div className={cn("rounded-xl border p-5", navyCard, navyBorder)}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className={cn("text-sm font-bold", silverBright)}>Intelligence Report</h3>
                  <p className="text-[10px] text-slate-500 mt-0.5">3 sheets · {sheet1Rows.length} issues · {sheet2Rows.length} brand-agg pairs · {sheet3Rows.length} chronic alerts</p>
                </div>
                <button onClick={generateIntelligenceReport}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold transition-colors shadow-lg shadow-indigo-900/40">
                  <Download size={14} /> Download TSV
                </button>
              </div>
            </div>

            {/* Sheet 1: Issue Reports */}
            <div className={cn("rounded-xl border overflow-hidden", navyCard, navyBorder)}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#334155]">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-indigo-400" />
                  <h3 className={cn("text-sm font-bold", silverBright)}>Sheet 1 — Issue Reports</h3>
                  <span className="text-[10px] text-indigo-400 font-semibold">{sheet1Rows.length} rows</span>
                </div>
              </div>
              <div className="overflow-x-auto max-h-[340px] overflow-y-auto">
                <table className="w-full text-left">
                  <thead className="sticky top-0 bg-[#1e293b] z-10">
                    <tr className="border-b border-[#334155]">
                      {['Ticket', 'Subject', 'Manager', 'Category', 'Brands', 'Aggregators', 'Issue Status', 'Resolution'].map(h => (
                        <th key={h} className={cn("px-4 py-2 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap", silverMuted)}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#334155]">
                    {sheet1Rows.length > 0 ? sheet1Rows.map((r, i) => (
                      <tr key={i} className="hover:bg-slate-800/30 transition-colors">
                        <td className="px-4 py-2"><span className="text-[10px] font-mono text-slate-400">{r.ticketId}</span></td>
                        <td className={cn("px-4 py-2 text-[11px] max-w-[180px] truncate", silverText)}>{r.subject}</td>
                        <td className="px-4 py-2 text-[11px] text-slate-400">{r.manager}</td>
                        <td className={cn("px-4 py-2 text-[11px] font-semibold", silverText)}>{r.category}</td>
                        <td className="px-4 py-2 text-[11px] text-slate-400 max-w-[140px] truncate">{r.brands}</td>
                        <td className="px-4 py-2 text-[11px] text-slate-400">{r.aggregators}</td>
                        <td className="px-4 py-2"><span className={cn("text-[10px] px-2 py-0.5 rounded-full font-semibold", ISSUE_REPORT_STATUS_COLORS[r.issueStatus].bg, ISSUE_REPORT_STATUS_COLORS[r.issueStatus].text)}>{r.issueStatusLabel}</span></td>
                        <td className="px-4 py-2 text-[11px] text-slate-500 whitespace-nowrap">{r.durationMs > 0 ? formatDuration(r.durationMs) : <span className="text-amber-400">Open</span>}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={8} className="px-4 py-8 text-center text-xs text-slate-500 italic">No issue reports found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Sheet 2: Brand × Aggregator Summary */}
            <div className={cn("rounded-xl border overflow-hidden", navyCard, navyBorder)}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#334155]">
                <div className="flex items-center gap-2">
                  <BarChart3 size={14} className="text-emerald-400" />
                  <h3 className={cn("text-sm font-bold", silverBright)}>Sheet 2 — Brand × Aggregator Summary</h3>
                  <span className="text-[10px] text-emerald-400 font-semibold">{sheet2Rows.length} pairs</span>
                </div>
              </div>
              <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                <table className="w-full text-left">
                  <thead className="sticky top-0 bg-[#1e293b] z-10">
                    <tr className="border-b border-[#334155]">
                      {['Brand', 'Aggregator', 'Total Issues', 'Resolved', 'Res. Rate', 'Avg Resolution'].map(h => (
                        <th key={h} className={cn("px-4 py-2 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap", silverMuted)}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#334155]">
                    {sheet2Rows.length > 0 ? sheet2Rows.map((r, i) => (
                      <tr key={i} className="hover:bg-slate-800/30 transition-colors">
                        <td className={cn("px-4 py-2 text-[11px] font-semibold", silverText)}>{r.brand}</td>
                        <td className="px-4 py-2 text-[11px] text-slate-400">{r.aggregator}</td>
                        <td className="px-4 py-2 text-[11px] text-slate-300 font-semibold">{r.totalIssues}</td>
                        <td className="px-4 py-2 text-[11px] text-emerald-400 font-semibold">{r.resolved}</td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${r.resRate}%` }} /></div>
                            <span className="text-[10px] text-slate-400">{r.resRate}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-[11px] text-slate-400">{r.avgHrs === 'N/A' ? <span className="text-slate-600">—</span> : `${r.avgHrs}h`}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-slate-500 italic">No summary data</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Sheet 3: Chronic Issues */}
            <div className={cn("rounded-xl border overflow-hidden", navyCard, navyBorder)}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#334155]">
                <div className="flex items-center gap-2">
                  <Repeat size={14} className="text-amber-400" />
                  <h3 className={cn("text-sm font-bold", silverBright)}>Sheet 3 — Chronic Issues &amp; Lost Hours</h3>
                  <span className="text-[10px] text-amber-400 font-semibold">{sheet3Rows.length} alerts</span>
                </div>
              </div>
              <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                <table className="w-full text-left">
                  <thead className="sticky top-0 bg-[#1e293b] z-10">
                    <tr className="border-b border-[#334155]">
                      {['Brand', 'Aggregator', 'Issue Type', 'Occurrences (30d)', 'Related Tickets', 'Lost Hours'].map(h => (
                        <th key={h} className={cn("px-4 py-2 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap", silverMuted)}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#334155]">
                    {sheet3Rows.length > 0 ? sheet3Rows.map((r, i) => (
                      <tr key={i} className="hover:bg-slate-800/30 transition-colors">
                        <td className={cn("px-4 py-2 text-[11px] font-semibold", silverText)}>{r.brand}</td>
                        <td className="px-4 py-2 text-[11px] text-slate-400">{r.aggregator}</td>
                        <td className="px-4 py-2 text-[11px] text-slate-300">{r.issueType}</td>
                        <td className="px-4 py-2"><span className="text-[11px] font-bold text-red-400">{r.occurrences}</span></td>
                        <td className="px-4 py-2 text-[10px] font-mono text-slate-500 max-w-[200px] truncate">{r.tickets}</td>
                        <td className="px-4 py-2"><span className="text-[11px] font-bold text-amber-400">{r.lostHrs}h</span></td>
                      </tr>
                    )) : (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-slate-500 italic">No chronic issues detected (requires &gt;3 occurrences in 30 days)</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Knowledge Base Tab --------------------------------------------------

interface KbArticle {
  id: string;
  title: string;
  category: string;
  content: string;
  updatedAt: number;
}

const STORAGE_KEY_KB = 'dm_support_kb_articles';

const DEFAULT_KB_ARTICLES: KbArticle[] = [
  { id: 'kb-1', title: 'How to Report an Aggregator Issue', category: 'Getting Started', content: 'Navigate to the Support Desk module, click "Create New Ticket", select the appropriate brands and aggregators, describe the issue in detail, and submit. Your ticket will be routed to the admin team automatically.', updatedAt: Date.now() },
  { id: 'kb-2', title: 'Understanding Issue Resolution Flow', category: 'Workflows', content: 'Once a ticket is submitted, it enters OPEN status. The admin team reviews each issue report individually. Issues can be Resolved or Rejected. The ticket auto-resolves when all issues are resolved.', updatedAt: Date.now() },
  { id: 'kb-3', title: 'SLA Targets and Response Times', category: 'Policies', content: 'First response target: 4 hours. Resolution target: 48 hours. SLA tracking begins from ticket creation. Critical issues involving multiple brands may receive expedited handling.', updatedAt: Date.now() },
  { id: 'kb-4', title: 'Multi-Issue Batching Guide', category: 'Getting Started', content: 'You can batch multiple issues into a single ticket. After configuring and describing the first issue, click "Save & Add Another Issue". Each issue gets its own configuration (brands, aggregators, category). Submit all issues together as one ticket.', updatedAt: Date.now() },
  { id: 'kb-5', title: 'Using Smart Aggregator Detection', category: 'Features', content: 'When you select brands, the system automatically detects which aggregators are linked to those brands through active campaigns. This saves time and ensures accuracy. If detection is unavailable, you can manually select aggregators.', updatedAt: Date.now() },
  { id: 'kb-6', title: 'Escalation Procedures', category: 'Policies', content: 'If your issue is not resolved within the SLA target, use the chat to @mention the support-admin team. For cross-module issues, mention the specific module team (e.g., @aggregator-module). Unresolved tickets beyond 72 hours are auto-escalated.', updatedAt: Date.now() },
];

function loadKbArticles(): KbArticle[] {
  try { const raw = localStorage.getItem(STORAGE_KEY_KB); return raw ? JSON.parse(raw) : DEFAULT_KB_ARTICLES; }
  catch { return DEFAULT_KB_ARTICLES; }
}
function saveKbArticles(articles: KbArticle[]) { localStorage.setItem(STORAGE_KEY_KB, JSON.stringify(articles)); }

const KB_CATEGORIES = ['Getting Started', 'Workflows', 'Policies', 'Features', 'General'];

function KnowledgeBaseTab({ user }: { user: User | null }) {
  const canEdit = hasTabVariant(user, 'support-desk', 'knowledge-base', 'admin');
  const [articles, setArticles] = useState<KbArticle[]>(() => loadKbArticles());
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formCategory, setFormCategory] = useState(KB_CATEGORIES[0]);
  const [formContent, setFormContent] = useState('');

  const persist = (updated: KbArticle[]) => { setArticles(updated); saveKbArticles(updated); };

  const openNewForm = () => {
    setEditingId(null);
    setFormTitle(''); setFormCategory(KB_CATEGORIES[0]); setFormContent('');
    setShowForm(true);
  };

  const openEditForm = (article: KbArticle) => {
    setEditingId(article.id);
    setFormTitle(article.title); setFormCategory(article.category); setFormContent(article.content);
    setShowForm(true);
    setExpandedId(null);
  };

  const saveForm = () => {
    if (!formTitle.trim() || !formContent.trim()) return;
    if (editingId) {
      persist(articles.map(a => a.id === editingId ? { ...a, title: formTitle.trim(), category: formCategory, content: formContent.trim(), updatedAt: Date.now() } : a));
    } else {
      const newArticle: KbArticle = { id: crypto.randomUUID(), title: formTitle.trim(), category: formCategory, content: formContent.trim(), updatedAt: Date.now() };
      persist([newArticle, ...articles]);
    }
    setShowForm(false); setEditingId(null);
  };

  const deleteArticle = (id: string) => {
    persist(articles.filter(a => a.id !== id));
    if (expandedId === id) setExpandedId(null);
  };

  const filtered = articles.filter(a =>
    a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.content.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="px-6 pb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Knowledge Base</h3>
          <p className="text-xs text-slate-400 mt-1">{articles.length} articles available</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search articles..."
              className="w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
          </div>
          {canEdit && (
            <button onClick={openNewForm} className="flex items-center gap-1.5 px-3.5 py-2 bg-indigo-600 text-white rounded-xl text-xs font-semibold hover:bg-indigo-700 transition-colors shadow-sm">
              <Plus size={14} /> New Article
            </button>
          )}
        </div>
      </div>

      {/* Create / Edit Form */}
      {showForm && canEdit && (
        <div className="bg-white rounded-2xl border border-indigo-200 shadow-sm p-5 mb-5 animate-config-slide-down">
          <h4 className="text-sm font-semibold text-slate-700 mb-4">{editingId ? 'Edit Article' : 'New Article'}</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Title</label>
              <input value={formTitle} onChange={e => setFormTitle(e.target.value)} placeholder="Article title..."
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Category</label>
              <div className="flex flex-wrap gap-2">
                {KB_CATEGORIES.map(cat => (
                  <button key={cat} onClick={() => setFormCategory(cat)}
                    className={cn("px-3 py-1.5 rounded-xl text-xs font-semibold border-2 transition-all",
                      formCategory === cat
                        ? "border-indigo-600 bg-indigo-50 text-indigo-700 shadow-sm"
                        : "border-slate-200 text-slate-500 hover:border-slate-300")}>
                    {cat}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Content</label>
              <textarea value={formContent} onChange={e => setFormContent(e.target.value)} placeholder="Article content..."
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 resize-none h-28" />
            </div>
            <div className="flex items-center gap-3 pt-1">
              <button onClick={saveForm} disabled={!formTitle.trim() || !formContent.trim()}
                className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm">
                <Check size={14} /> {editingId ? 'Update' : 'Publish'}
              </button>
              <button onClick={() => { setShowForm(false); setEditingId(null); }}
                className="px-4 py-2 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map(article => (
          <div key={article.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden transition-all">
            <button onClick={() => setExpandedId(expandedId === article.id ? null : article.id)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center"><FileText size={14} className="text-indigo-600" /></div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-700">{article.title}</h4>
                  <span className="text-[10px] text-slate-400">{article.category}</span>
                </div>
              </div>
              <ChevronDown size={14} className={cn("text-slate-400 transition-transform", expandedId === article.id && "rotate-180")} />
            </button>
            {expandedId === article.id && (
              <div className="px-4 pb-4 pt-0 border-t border-slate-100">
                <p className="text-xs text-slate-600 leading-relaxed mt-3 whitespace-pre-wrap">{article.content}</p>
                {canEdit && (
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
                    <button onClick={() => openEditForm(article)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                      <Pencil size={12} /> Edit
                    </button>
                    <button onClick={() => deleteArticle(article.id)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-sm text-slate-400">No articles match your search.</div>
        )}
      </div>
    </div>
  );
}

// --- App Settings Tab (Support Command Center) ---------------------

function AppSettingsTab({ user }: { user: User | null }) {
  const canEdit = hasTabVariant(user, 'support-desk', 'settings', 'admin');
  const [config, setConfig] = useState<SupportDeskConfig>(() => loadDeskConfig());
  const [newIssueType, setNewIssueType] = useState('');
  const [newArea, setNewArea] = useState('');
  const [saved, setSaved] = useState(false);

  const save = (updated: SupportDeskConfig) => {
    setConfig(updated);
    saveDeskConfig(updated);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const addIssueType = () => {
    const label = newIssueType.trim();
    if (!label || config.issueTypes.some(t => t.label.toLowerCase() === label.toLowerCase())) return;
    save({ ...config, issueTypes: [...config.issueTypes, { id: label.toLowerCase().replace(/\s+/g, '_'), label, icon: 'AlertCircle' }] });
    setNewIssueType('');
  };

  const removeIssueType = (id: string) => {
    save({ ...config, issueTypes: config.issueTypes.filter(t => t.id !== id) });
  };

  const addArea = () => {
    const label = newArea.trim();
    if (!label || config.systemAreas.some(a => a.label.toLowerCase() === label.toLowerCase())) return;
    save({ ...config, systemAreas: [...config.systemAreas, { id: label.toLowerCase().replace(/\s+/g, '_'), label }] });
    setNewArea('');
  };

  const removeArea = (id: string) => {
    save({ ...config, systemAreas: config.systemAreas.filter(a => a.id !== id) });
  };

  return (
    <div className="px-6 pb-8 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Support Command Center</h3>
          <p className="text-xs text-slate-400 mt-1">Configure issue types, system areas, and SLA targets</p>
        </div>
        {saved && <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold animate-pulse">Saved</span>}
      </div>

      {/* Issue Types CRUD + Critical Toggle */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
        <h4 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-2"><Tag size={14} className="text-indigo-600" /> Issue Types</h4>
        <p className="text-[10px] text-slate-400 mb-3">Toggle the flame icon to mark a category as critical</p>
        <div className="space-y-2 mb-3">
          {config.issueTypes.map(t => {
            const isCritical = config.criticalCategories.includes(t.id);
            return (
              <div key={t.id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <CategoryIcon name={t.icon} className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-xs font-medium text-slate-700">{t.label}</span>
                  {isCritical && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-600 text-[9px] font-bold">Critical</span>}
                </div>
                <div className="flex items-center gap-2">
                  {canEdit && (
                    <button onClick={() => {
                      const updated = isCritical
                        ? config.criticalCategories.filter(c => c !== t.id)
                        : [...config.criticalCategories, t.id];
                      save({ ...config, criticalCategories: updated });
                    }} title={isCritical ? 'Remove Critical' : 'Mark Critical'}
                      className={cn("p-1 rounded-lg transition-colors", isCritical ? "text-red-500 bg-red-50 hover:bg-red-100" : "text-slate-300 hover:text-red-400 hover:bg-red-50")}>
                      <Flame size={13} />
                    </button>
                  )}
                  {canEdit && (
                    <button onClick={() => removeIssueType(t.id)} className="text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <input value={newIssueType} onChange={e => setNewIssueType(e.target.value)} onKeyDown={e => e.key === 'Enter' && addIssueType()}
              placeholder="New issue type..." className="flex-1 px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
            <button onClick={addIssueType} className="px-3 py-2 bg-indigo-600 text-white rounded-xl text-xs font-semibold hover:bg-indigo-700 transition-colors">
              <Plus size={14} />
            </button>
          </div>
        )}
      </div>

      {/* System Areas CRUD */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Layers size={14} className="text-indigo-600" /> System Areas</h4>
        <div className="space-y-2 mb-3">
          {config.systemAreas.map(a => (
            <div key={a.id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-xl">
              <span className="text-xs font-medium text-slate-700">{a.label}</span>
              {canEdit && (
                <button onClick={() => removeArea(a.id)} className="text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
              )}
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <input value={newArea} onChange={e => setNewArea(e.target.value)} onKeyDown={e => e.key === 'Enter' && addArea()}
              placeholder="New system area..." className="flex-1 px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
            <button onClick={addArea} className="px-3 py-2 bg-indigo-600 text-white rounded-xl text-xs font-semibold hover:bg-indigo-700 transition-colors">
              <Plus size={14} />
            </button>
          </div>
        )}
      </div>

      {/* SLA & Toggles */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
        <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"><Clock size={14} className="text-indigo-600" /> SLA & Automation</h4>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">First Response Target (minutes)</label>
            <input type="number" min={1} value={config.slaTargets.firstResponse} disabled={!canEdit}
              onChange={e => save({ ...config, slaTargets: { ...config.slaTargets, firstResponse: Math.max(1, Number(e.target.value)) } })}
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-50" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Resolution Target (minutes)</label>
            <input type="number" min={1} value={config.slaTargets.resolution} disabled={!canEdit}
              onChange={e => save({ ...config, slaTargets: { ...config.slaTargets, resolution: Math.max(1, Number(e.target.value)) } })}
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-50" />
            <span className="text-[10px] text-slate-400 mt-0.5 block">Tickets exceeding this are marked Overdue</span>
          </div>
        </div>
        <div className="space-y-3">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-xs text-slate-600">Auto-assign tickets to available staff</span>
            <button onClick={() => canEdit && save({ ...config, autoAssign: !config.autoAssign })}
              className={cn("w-10 h-5 rounded-full transition-colors relative", config.autoAssign ? "bg-indigo-600" : "bg-slate-300")}>
              <span className={cn("absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform", config.autoAssign ? "translate-x-5" : "translate-x-0.5")} />
            </button>
          </label>
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-xs text-slate-600">Email notifications for new tickets</span>
            <button onClick={() => canEdit && save({ ...config, emailNotifications: !config.emailNotifications })}
              className={cn("w-10 h-5 rounded-full transition-colors relative", config.emailNotifications ? "bg-indigo-600" : "bg-slate-300")}>
              <span className={cn("absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform", config.emailNotifications ? "translate-x-5" : "translate-x-0.5")} />
            </button>
          </label>
        </div>
      </div>

      {/* Auto-Close Timer Durations */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h4 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-2"><Hourglass size={14} className="text-indigo-600" /> Auto-Close Timer Options</h4>
        <p className="text-[10px] text-slate-400 mb-3">Durations shown when resolving a ticket</p>
        <div className="space-y-2 mb-3">
          {config.autoCloseDurations.map((d, idx) => (
            <div key={idx} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-2">
                <Clock size={13} className="text-slate-400" />
                <span className="text-xs font-medium text-slate-700">{d.label}</span>
                <span className="text-[10px] text-slate-400">({d.hours}h)</span>
              </div>
              {canEdit && config.autoCloseDurations.length > 1 && (
                <button onClick={() => {
                  save({ ...config, autoCloseDurations: config.autoCloseDurations.filter((_, i) => i !== idx) });
                }} className="text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
              )}
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <input id="new-duration-label" placeholder="Label (e.g. 12 Hours)" className="flex-1 px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
            <input id="new-duration-hours" type="number" min={1} placeholder="Hours" className="w-20 px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
            <button onClick={() => {
              const labelEl = document.getElementById('new-duration-label') as HTMLInputElement | null;
              const hoursEl = document.getElementById('new-duration-hours') as HTMLInputElement | null;
              if (!labelEl || !hoursEl) return;
              const label = labelEl.value.trim();
              const hours = Number(hoursEl.value);
              if (!label || !hours || hours < 1) return;
              save({ ...config, autoCloseDurations: [...config.autoCloseDurations, { label, hours }] });
              labelEl.value = ''; hoursEl.value = '';
            }} className="px-3 py-2 bg-indigo-600 text-white rounded-xl text-xs font-semibold hover:bg-indigo-700 transition-colors">
              <Plus size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


export default SupportDeskModule;
